import { Valmiusilmoitus } from "./Valmiusilmoitus";
import { puuteKuvausTeksti } from "./puutekuvausTeksti";
import { useLataus, LatausTila } from "./Lataus";
import { useKohde, useKohdeSynkka } from "./Kohdekonteksti";
import { naytaHitsinumero } from "./HitsiTunnus";
import { jarjestaHitsit } from "./hitsijarjestys";
import { Ohje } from "./Ohje";
import { naytaPvm } from "./pvm";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useViimeisin } from "./tuoreus";
import { LinjaLinkki } from "./Linkit";
import HitsiaEiTehda from "./HitsiaEiTehda";
import { hitsinHuomio } from "./hitsihuomio";
import { nayttonimi, nayttonimiPieni, selite } from "./nayttonimi";
import {
  api, ApiError,
  type Kayttaja, type HoitajaSuodatin, type Linja, type Hitsi, type Hitsityyppi, type NdtKattavuus,
  type Punakyna, type HyvaksyjanAsema, HYVAKSYJAN_ASEMAT,
  type Kohde,
  type Puute,
} from "./api";
import Liitteet, { lueBase64 } from "./Liitteet";
import { KorvaavaKuittaus, KorvausMerkinta, saaKorvata } from "./KorvaavaKuittaus";
import { Painekoe } from "./Painekoe";
import { VtHylkays, VtJoukkoIkkuna, VtSolu, vtHylatty, vtValittavissa } from "./VtKirjaus";
import { NdtLomake, NdtRivi } from "./NdtKirjaus";
import Punchit from "./Punchit";
import Laippaliitokset from "./Laippaliitokset";
import PunchSelailu from "./PunchSelailu";
import HitsauslokiMuokkaus from "./HitsauslokiMuokkaus";
import PohjaTuontiPaneeli from "./PohjaTuonti";
import Napparivi from "./Napparivi";
import LinjaTarkastus, { LinjatarkastusKuvat } from "./LinjaTarkastus";
import IsometriMerkinnat from "./IsometriMerkinnat";
import IsometriTuonti from "./IsometriTuonti";
import { useOsoiteHaku, lueHaku, haunLuku } from "./osoitehaku";
import { Tilasana } from "./Tila";
import { KorjauksenSyyValinta, korjauksenSyy } from "./HitsinKorjaus";
import { t, virheTeksti } from "./kieli";
import { Kenttavirhe, useKenttavirheet } from "./Kenttavirhe";
import HyvaksyjanTaho, { hyvaksyjanEsitys } from "./HyvaksyjanTaho";

/* ═══════════════════════════════════════════════════════════
   Hitsauslokin ja linjojen selailu.

   Lukunäkymä: kaikki linjat, ja linjaa klikkaamalla sen hitsit.
   Ei kirjausta — kirjaus on erikseen Hitsausloki-näkymässä. Tämä on
   työnjohdon ja toimiston ikkuna siihen mitä on kirjattu.
   ═══════════════════════════════════════════════════════════ */

/** Punakynän talon työnjohto (vahvistaa ja perii ilman oikeutta) — sama lista kuin palvelimen vaadiPunakynaHyvaksyja.
 *  Hitsauksen työnjohtaja lisätty 26.10.2026 (Toni klo 06.15). */
const PUNAKYNAN_TYONJOHTO = ["paakayttaja", "suunnittelija", "tyonjohtaja", "hitsauksen_tyonjohtaja"];
const hitsityyppiNimi = (k: string): string =>
  k === "A" ? t("hl.laji.A") : k === "E" ? t("hl.laji.E") : k === "R" ? t("hl.laji.R") : k === "S" ? t("sel.laji_s") : k;

export default function Selailu({
  kayttaja, osoiteKohde, asetaOsoiteKohde, osoiteHaku, asetaOsoiteHaku, vainVahvistettavat = false, laippaliitokset = false,
}: {
  kayttaja: Kayttaja;
  /** 264 (T636): moduuli «Laippaliitokset» päällä — linjasivun laippaosio. Kuori päättää (paalla), ei tämä näkymä. */
  laippaliitokset?: boolean;
  /** T488: tilaajan valvojan näkymä — vain «Odottaa vahvistusta» -jono, ei linjalistaa, hakua eikä tuonteja. */
  vainVahvistettavat?: boolean;
  /** Osoitteen `#/selailu/<tunnus>` -hanta, tai null. */
  osoiteKohde?: string | null;
  asetaOsoiteKohde?: (tunnus: string | null) => void;
  /** Osoitteen kyselyosa: `nayta=punchit&kohde=<id>&vain_avoimet=1`.
   *  Projekti-Hubin luvut osoittavat tänne, ks. osoitehaku.ts. */
  osoiteHaku?: string;
  asetaOsoiteHaku?: (haku: string) => void;
}) {
  /*
   * Neljä eri oikeutta, neljä lippua — vaikka kolmen roolilista sattuu
   * olemaan sama.
   *
   * Yksi lippu kolmelle oikeudelle oli se vika joka piilotti
   * kenttäkirjauksen (Hitsausloki `saaMuokata`): kukaan ei lue riviä
   * kysyäkseen "saako työnjohtaja tuoda Excelin", jos rivi ei puhu
   * Excelistä. Jokainen lippu nimetään sen mukaan mitä se avaa, ja
   * jokaisen lista on sama kuin sen reitin `vaadiRooli`.
   */

  /**
   * PATCH /api/hitsit — hitsin korjaus (T309, Toni 14.9.2026): kenttätyönjohto ja ylöspäin. Rivikohtainen
   * lupa on palvelimen `korjattavissa` (kenttätekijä omat ja parin hitsit 24 h kirjauksesta); tämä lippu
   * vain varaa sarakkeen. Selailu ei ole kenttätekijän valikossa, joten kenttätekijä korjaa Hitsauslokista.
   */
  const saaMuokataHitseja = kayttaja.roolit.some((r) =>
    ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r));
  /* 152: punakynän hyväksyntä on OIKEUS eikä rooli — hyväksyjä voi olla
     tilaajan valvoja, joka on rooliltaan lukija. Sama ehto kuin
     palvelimella (`vaadiOikeus`); selain ei päättele omaa versiotaan. */
  /* 10.10.2026 (Testaus-ohjeen tarkistus): palvelin (vaadiPunakynaHyvaksyja) sallii talon työnjohdon — pääkäyttäjä,
     suunnittelija, työnjohtaja — vahvistaa ILMAN oikeutta (155: «työ ei saa stopata siihen»), mutta nappi näkyi vain
     oikeuden haltijalle. Työnjohtaja ei siis voinut vahvistaa kentän alinumeroa käyttöliittymästä. Sama ehto kuin palvelimella. */
  const saaPunakyna = kayttaja.oikeudet.includes("punakyna_hyvaksynta")
    // T488: kohteeseen rajattu oikeus (163) kelpaa — palvelin tarkistaa hitsin kohteen (vaadiPunakynaHyvaksyja).
    || (kayttaja.kohdeOikeudet?.["punakyna_hyvaksynta"] ?? []).length > 0
    || kayttaja.roolit.some((r) => PUNAKYNAN_TYONJOHTO.includes(r));
  /* ⛔ ESITYS ON ERI ASIA KUIN MUOKKAUS (Toni 21.9.2026: «Selailusta ei
     voi muokata; punakynän esitykset ovat sallittuja kuten
     kenttäkirjauksessa»). Kenttätekijä on se joka huomaa oikaisun, ja
     Selailu on se näkymä jossa linjan saumat näkyvät kokonaisuutena.
     Muokkausliput (`saaMuokataHitseja`, `saaMuokataLinjaa`,
     `saaPoistaaHitseja`, `saaTuodaExcelin`, `saaKirjata`) eivät sisällä
     kenttäroolia, joten muokkaus pysyy kiinni ilman erillistä kieltoa. */
  const saaEsittaa = saaMuokataHitseja || saaPunakyna
    || kayttaja.roolit.some((r) => r === "kenttatekija" || r === "hitsaaja");
  const [eiTehdaAuki, setEiTehdaAuki] = useState(false);
  // Toni 19.10.2026 klo 11.50: tarkastuskuvat isometrin yläpuolella; muutos päivittää lomakkeen luovutustilan (paperipöytäkirja).
  const [kuvaversio, setKuvaversio] = useState(0);
  const [punakyna, setPunakyna] = useState<Hitsi | null>(null);
  /** 194: historia (ja hyväksynnän peruutus) tilasanasta — punakynä-nappi on vain vahvistus. */
  const [punakynaHistoria, setPunakynaHistoria] = useState<Hitsi | null>(null);
  /* 153: «+ hitsi väliin» — kentällä tehty lisäys alinumerolla. Sama
     rooli kuin hitsin kirjauksella; hyväksyntä on eri asia ja eri
     ihminen. */
  const [valiin, setValiin] = useState<Hitsi | null>(null);
  /* «Ei toteudu» on punakynän toinen puoli: sauma jää tekemättä. Sama
     tasomalli kuin lisäyksellä (155) — työ ei pysähdy hyväksyntään. */
  const [eiToteudu, setEiToteudu] = useState<Hitsi | null>(null);

  /** 218 (Toni 10.10.2026 «Tee kaikki 4»): poisto on aina esitys + vahvistus, myös pääkäyttäjälle. Esityksen tekee
   *  sama joukko kuin muokkauksen; vahvistaa tai hylkää työnjohtaja tai pääkäyttäjä — ei koskaan esittäjä itse
   *  (palvelin vastaa 409 «oma_esitys»). */
  const saaVahvistaaPoiston = kayttaja.roolit.some((r) => r === "paakayttaja" || r === "tyonjohtaja");
  const [poisto, setPoisto] = useState<Hitsi | null>(null);

  /** PATCH /api/linjat/:id — linjan tiedot ja painekoe. */
  const saaMuokataLinjaa = kayttaja.roolit.some((r) =>
    ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r));

  /** POST /api/hitsausloki/tuonti — Excel-korjauskierto. */
  const saaTuodaExcelin = kayttaja.roolit.some((r) =>
    ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r));

  /** PUNCH_KIRJURI + PUT /api/linjat/:id/linjatarkastus. */
  const saaKirjata = kayttaja.roolit.some((r) =>
    ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r));
  /** T525 (Toni 11.10.2026): punch-lista ja sen kuittaus myös tarkastajalle — sama roolijoukko kuin API:n PUNCH_KIRJURI. */
  const saaKirjataPunch = saaKirjata || kayttaja.roolit.includes("tarkastaja");
  /** T636: laippakiristyksen kirjaus — oma työlaji laippatyö (263); palvelin tarkistaa lisäksi pätevyyden. Hyväksyntä: sama
   *  roolijoukko kuin API:n KASITTELIJAN_ROOLIT (laippakiristys.ts). */
  //  Toni 25.10.2026 klo 20.00 ja 20.20 (päätös 3): pääkäyttäjä ja työnjohto kirjaavat toisen puolesta ja valitsevat kiristäjän
  //  (sama joukko kuin API:n KIRISTAJAN_VALITSIJAT); ei suunnittelijalle eikä kenttätilille.
  const kiristaaToisenPuolesta = !kayttaja.kenttatili && kayttaja.roolit.some((r) => ["paakayttaja", "tyonjohtaja", "hitsauksen_tyonjohtaja"].includes(r));
  const saaKiristaa = kayttaja.laippatyo === true || kiristaaToisenPuolesta;
  const saaKasitellaKiristyksen = kayttaja.roolit.some((r) => ["paakayttaja", "tyonjohtaja", "hitsauksen_tyonjohtaja", "tarkastaja"].includes(r));
  /** DELETE /api/liitteet/:id: pääkäyttäjä ja suunnittelija. Eri lippu kuin lisäys. */
  const saaPoistaaLiitteen = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija"].includes(r));
  /** T280: POST /api/linjat/tuonti (pääkäyttäjä, suunnittelija) ja /api/wps/tuonti (sama, ei rajatulle). */
  const saaTuodaLinjat = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija"].includes(r));
  // T488: hitsauskoordinointi — NDT-vaatimukset kohteeseen (myös kohteeseen rajattu oikeus), WPS vain koko yrityksen oikeudella.
  const saaTuodaNdt = saaTuodaLinjat || (kayttaja.oikeudet.includes("hitsauskoordinointi") || (kayttaja.kohdeOikeudet?.["hitsauskoordinointi"] ?? []).length > 0);
  const saaTuodaWps = (saaTuodaLinjat && kayttaja.kohteet.length === 0) || kayttaja.oikeudet.includes("hitsauskoordinointi");

  const [linjat, setLinjat] = useState<Linja[] | null>(null);
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  /* Kohdesuodatin, punch-tila ja "vain avoimet" alustetaan
     OSOITTEESTA, eivät oletuksesta: ne määrittelevät sen joukon jonka
     Hubin luku nimeää (osoitehaku.ts). Alustus useState-alustajassa
     on pakollinen, ei mukavuus — ks. hookin otsikkokommentti. */
  const alkuHaku = lueHaku(osoiteHaku);
  const [kohde, setKohde] = useState<number | "">(() => haunLuku(alkuHaku, "kohde"));
  // T426: yläpalkin kohdevalinta ohjaa; osoitteen ?kohde= (Hubin linkki) vaihtaa yläpalkin.
  const { aseta: asetaYlakohde } = useKohde();
  const { ylapalkissa, ylaValittu } = useKohdeSynkka(kohde, setKohde,
    { osoitteenKohde: haunLuku(lueHaku(osoiteHaku), "kohde"), kaikkiSallittu: true });
  const [haku, setHaku] = useState("");
  const [valittu, setValittu] = useState<Linja | null>(null);
  const [hitsit, setHitsit] = useState<Hitsi[] | null>(null);
  /* T469 (10.10.2026): puutteen linkki vie hitsiin — `#/selailu/<linja>?hitsi=<id>` avaa linjan, vierittää hitsin
     riville ja korostaa sen. Ei suodatin: linjan muut hitsit näkyvät, jotta hitsi nähdään yhteydessään. */
  const [korostaHitsi] = useState<number | "">(() => haunLuku(alkuHaku, "hitsi"));
  useEffect(() => {
    if (korostaHitsi === "" || !hitsit) return;
    const rivi = document.querySelector(`tr[data-hitsi="${korostaHitsi}"]`);
    if (rivi) { rivi.setAttribute("data-korostettu", ""); rivi.scrollIntoView({ block: "center" }); }
  }, [korostaHitsi, hitsit]);
  const [kattavuus, setKattavuus] = useState<NdtKattavuus[] | null>(null);
  const [kaikki, setKaikki] = useState(false);
  // T677 F (Toni 27.10.2026 klo 09.08): suodatin «Vain suunnitellut hitsit» — palvelin rajaa (vaihe = suunniteltu).
  const [vainSuunnitellut, setVainSuunnitellut] = useState(false);
  const suunVaihe = vainSuunnitellut ? "suunniteltu" as const : undefined;
  const [punchNakyma, setPunchNakyma] = useState(() => alkuHaku.get("nayta") === "punchit");
  /* 194 (päätös 62 kohta 5): «Odottaa vahvistusta» — rivit joilla on vahvistamaton muutos, yli linjojen.
     Hubin luku osoittaa tänne (`nayta=vahvistettavat`), ja joukko on sama SQL-lauseke kuin Hubissa. */
  const [vahvistettavatNakyma, setVahvistettavatNakyma] = useState(() => vainVahvistettavat || alkuHaku.get("nayta") === "vahvistettavat");
  // Oletus on "vain avoimet" kun punch-tila avataan napista; osoitteesta
  // tultaessa lippu on se mitä osoite sanoo, ja poissaolo on "kaikki".
  const [vainAvoimet, setVainAvoimet] = useState(() =>
    alkuHaku.get("nayta") !== "punchit" || alkuHaku.get("vain_avoimet") === "1");
  // 236 (T534): punch-listan hoitajasuodatin osoitteessa — osoitusviestin linkki tuo ?hoitaja=minun.
  const [punchHoitaja, setPunchHoitaja] = useState<HoitajaSuodatin | "">(() => {
    const h = alkuHaku.get("hoitaja"); return h === "minun" || h === "ei" ? h : ""; });
  const [muokattava, setMuokattava] = useState<number | null>(null);
  /* VT-joukkokirjaus ja pikakirjaus (11.10.2026): sama roolijoukko kuin POST /api/hitsit/vt ja yksittäinen NDT-kirjaus. */
  const saaKirjataVt = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija", "tyonjohtaja", "tarkastaja"].includes(r));
  const [vtValitut, setVtValitut] = useState<Set<number>>(new Set());
  const [vtHylkays, setVtHylkays] = useState<number | null>(null);
  const [vtPikaTulos, setVtPikaTulos] = useState<"hyvaksytty" | "hylatty">("hylatty");   // T582: «VT ✓» kysyy pöytäkirjan
  const [ndtAuki, setNdtAuki] = useState<number | null>(null);   // T525
  const [vtIkkuna, setVtIkkuna] = useState(false);
  const [vtKaynnissa, setVtKaynnissa] = useState(false);   // «VT ✓» tallentaa suoraan (Toni 16.10.2026 klo 05.20)
  const [vtIlmoitus, setVtIlmoitus] = useState<string | null>(null);
  const [kuvaHitsi, setKuvaHitsi] = useState<number | null>(null);
  // Tarra-sarake vain kun tarran etuliite on asetettu (T333, Toni 14.9.2026: tarra vain JSG:llä). Sama asetus ja sama
  // haku kuin Hitsauslokin kirjauslomakkeessa; epäonnistunut haku piilottaa sarakkeen, data ei katoa kannasta.
  const [tarraNakyy, setTarraNakyy] = useState(false);
  useEffect(() => {
    api.hitsauslokiTilanne()
      .then((t) => setTarraNakyy(!!(t.tarra_etuliite ?? "").trim()))
      .catch(() => setTarraNakyy(false));
  }, []);
  const [jarjestys, setJarjestys] =
    useState<{ sarake: string; suunta: "nouseva" | "laskeva" } | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [linjaMuokkausAuki, setLinjaMuokkausAuki] = useState(false);

  /* Suodatin ↔ osoite. `vain_avoimet` menee osoitteeseen vain punch-
     tilassa, koska se ei tarkoita mitään linjalistalle. */
  useOsoiteHaku(osoiteHaku, asetaOsoiteHaku,
    { nayta: punchNakyma ? "punchit" : vahvistettavatNakyma ? "vahvistettavat" : "", kohde, vain_avoimet: punchNakyma && vainAvoimet,
      hoitaja: punchNakyma ? punchHoitaja : "" },
    (p) => {
      /* 17.10.2026 (Toni klo 12.05): osoite ilman kohdetta ei ylikirjoita yläpalkin kohdetta — valikosta avattuna mountin
       osoitesynkka ajoi yläpalkin koukun jälkeen ja palautti kohteeksi «kaikki», jolloin näkymä ja yläpalkki jäivät eri mieltä. */
      if (p.has("kohde")) setKohde(haunLuku(p, "kohde"));
      const punch = p.get("nayta") === "punchit";
      setPunchNakyma(punch);
      const vahv = p.get("nayta") === "vahvistettavat";
      setVahvistettavatNakyma(vahv);
      if (vahv) { setKaikki(false); setValittu(null); setMuokattava(null); }
      if (punch) {
        setVainAvoimet(p.get("vain_avoimet") === "1");
        const h = p.get("hoitaja"); setPunchHoitaja(h === "minun" || h === "ei" ? h : "");
        setKaikki(false); setValittu(null); setMuokattava(null);
      }
    });
  useEffect(() => { setLinjaMuokkausAuki(false); }, [valittu]);
  /* T396 (Toni 30.9.2026 «a»): vapaatekstikenttä «Painekoe (linja)» on piilotettu.
     `linja.painekoe` siirrettiin rakenteiseen kokeeseen 30.9.2026 (painekoesiirto,
     pekilo 3 luotua · demo 0), ja painekoe kirjataan «NDT ja painekoe» -lohkossa.
     Sarake jää kantaan, kenttää ei näytetä eikä kirjoiteta. */

  function asetaJarjestys(sarake: string) {
    setJarjestys((j) => j && j.sarake === sarake
      ? { sarake, suunta: j.suunta === "nouseva" ? "laskeva" : "nouseva" }
      : { sarake, suunta: "nouseva" });
  }

  // Järjestetty näkymä. Numeeriset sarakkeet (nro, DN) vertaillaan
  // lukuina, muut tekstinä — muuten "10" tulisi ennen "2".
  const jarjestetyt = useMemo(() => {
    if (!hitsit) return hitsit;
    // Oletus (Tonin havainto 11.10.2026: lista oli luontijärjestyksessä, lehti /2 ennen /1): lehti ja sitten numero
    // NUMEERISESTI — 1, 1.1, 2, 10; ei merkkijonojärjestystä. Sama järjestys kuin virallisessa lokissa.
    // T641 (Toni 20.10.2026 klo 16.39): yhteinen funktio — tyhjä lehti ja paikanpitäjät loppuun, R alkuperäisen perään.
    if (!jarjestys) return jarjestaHitsit(hitsit);
    const arvo = (h: Hitsi): string | number => {
      switch (jarjestys.sarake) {
        case "linja":    return h.linja;
        case "media":    return (h.linja ?? "").split("-")[2] ?? "";
        case "nro":      return h.hitsinumero;
        case "lehti":    return h.piirustuslehti;
        case "revisio":  return h.revisio ?? "";
        case "tyyppi":   return h.hitsityyppi ?? "";
        case "vaihe":    return h.vaihe;
        case "paiva":    return h.paiva ?? "";
        case "dn":       return h.dn ?? -1;
        case "hitsaaja": return h.juuri_hitsaaja_koodi ?? "";
        default:         return "";
      }
    };
    const kerroin = jarjestys.suunta === "nouseva" ? 1 : -1;
    return [...hitsit].sort((a, b) => {
      const av = arvo(a), bv = arvo(b);
      const an = typeof av === "number" ? av : Number(av);
      const bn = typeof bv === "number" ? bv : Number(bv);
      if (av !== "" && bv !== "" && !Number.isNaN(an) && !Number.isNaN(bn)) {
        return (an - bn) * kerroin;
      }
      return String(av).localeCompare(String(bv), "fi") * kerroin;
    });
  }, [hitsit, jarjestys]);

  const nuoli = (a: string) =>
    jarjestys?.sarake === a ? (jarjestys.suunta === "nouseva" ? " ▲" : " ▼") : "";

  const klKohteet = useLataus();
  useEffect(() => { klKohteet.seuraa(api.kohteet().then((k) => setKohteet(k as Kohde[]))); }, [klKohteet.kerta]);

  // Haku näppäillessä (Toni 6.10.2026): 200 ms viive, ja vain viimeisimmän haun vastaus kirjoittaa listan (päätös 89/99).
  const uusiLinjahaku = useViimeisin();
  const lataaLinjat = useCallback(async (h: string, k: number | "") => {
    const tuore = uusiLinjahaku();
    try { const l = await api.linjat(h || undefined, k || undefined, 1000); if (!tuore()) return; setLinjat(l); setVirhe(null); }
    catch (e) {
      if (!tuore()) return;
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("nippu.haku_virhe"));
      setLinjat([]);
    }
  }, [uusiLinjahaku]);
  // T488: valvojan näkymä ei lataa linjoja (reitti on tilaajalta kiinni).
  useEffect(() => { if (vainVahvistettavat) { setLinjat([]); return; } const t = setTimeout(() => void lataaLinjat(haku, kohde), haku ? 200 : 0); return () => clearTimeout(t); }, [haku, kohde, lataaLinjat, vainVahvistettavat]);

  /*
   * Osoitteesta tullut linja avataan kun lista on ladattu.
   *
   * ⛔ JAETTU LINKKI EI SAA PUDOTA HILJAA. Jos tunnusta ei loydy --
   * sita ei ole, tai vastaanottaja ei nae sita kohdetta -- naytolle
   * tulee NAKYVA syy. Oletusnakymaan palaaminen kertoisi
   * vastaanottajalle etta linkki toimi, ja se on pahempi kuin
   * rikkinainen linkki.
   *
   * Monta osumaa on oma tilansa: `UNIQUE (kohde_id, tunnus)` sallii
   * saman tunnuksen kahdessa kohteessa. Mitattu 31.8.2026: nolla
   * tormaysta 844 linjassa. Silloin kun se joskus tapahtuu,
   * valitsemme naytettavan epaselvyyden hiljaisen arvauksen sijaan.
   */
  const [osoiteVirhe, setOsoiteVirhe] = useState<string | null>(null);
  const tarkistettuOsoite = useRef<string | null>(null);
  useEffect(() => {
    if (!osoiteKohde || !linjat) return;
    if (valittu?.tunnus === osoiteKohde) return;
    const osumat = linjat.filter((l) => l.tunnus === osoiteKohde);
    if (osumat.length === 1) {
      setOsoiteVirhe(null); void avaaLinja(osumat[0]!);
      // T584 (Toni 15.10.2026 klo 18.10): linja löytyi listasta, mutta yläpalkissa on toinen kohde (lista «kaikki kohteet») →
      // yläpalkki vaihtuu linjan kohteeksi tallentamatta, kuten osoitteen ?kohde=. Protot'n kävely 13.10. ja 15.10.: 16 vs 19.
      if (ylapalkissa && osumat[0]!.kohde_id !== ylaValittu) void asetaYlakohde(osumat[0]!.kohde_id, false);
    }
    /* Toni 31.10.2026 klo 16.05 ja 17.15: hakukenttä suodattaa listan, joten osoitteen linjan puuttuminen suodatetusta listasta
       ei kerro mitään sen olemassaolosta — linja tarkistetaan aina palvelimelta. Saman osoitteen tarkistusta ei toisteta jokaisella
       näppäilyllä (muuten olemassa oleva linja tyhjentäisi haun kesken kirjoituksen); uusi osoite tarkistetaan aina. */
    else if (osumat.length === 0 && haku.trim() && tarkistettuOsoite.current === osoiteKohde) return;
    else if (osumat.length === 0) {
      tarkistettuOsoite.current = osoiteKohde;
      /* 124: tunnus ei ole listassa — onko se korvattu? Korvattu tunnus OHJAA uuteen
       * (osoite vaihtuu uuteen tunnukseen) ja sanoo sen ääneen; passiivinen ilman
       * korvaajaa ja tuntematon saavat kumpikin oman lauseensa. */
      void (async () => {
        try {
          const r = (await api.linjaTunnuksella(osoiteKohde)).find((x) => x.korvaaja_tunnus) ?? (await api.linjaTunnuksella(osoiteKohde))[0];
          if (r?.korvaaja_tunnus) {
            const uusi = linjat.find((l) => l.tunnus === r.korvaaja_tunnus);
            const lause = t("sel.korvattu", {
              vanha: osoiteKohde, uusi: r.korvaaja_tunnus,
              miten: r.korvaus_mekanismi === "lahde:master:ratkaisu" ? t("sel.lahde_ratkaisi") : r.korvaus_mekanismi ?? "?",
              pvm: naytaPvm(r.korvaus_havaittu) ?? "?" });
            if (uusi) { await avaaLinja(uusi); setOsoiteVirhe(lause); }
            else setOsoiteVirhe(lause + " " + t("sel.uusi_ei_listassa"));
          } else if (r && r.aktiivinen && r.kohde_id !== kohde) {
            /* 11.10.2026: linja on olemassa ja näkyvissä (reitti rajaa kohteittain), mutta eri kohteessa kuin yläpalkin
               valinta. Ennen tätä linkki toisesta kohteesta (Puutteet, Luovutusvalmius, viesti) sanoi «Linjaa ei löydy».
               Kohde vaihtuu linjan kohteeksi, linjalista latautuu ja tämä efekti avaa linjan. Yläpalkin valinta vaihdetaan
               samalla tavalla kuin osoitteen ?kohde= (tallentamatta), ja näkymä seuraa yläpalkkia — ennen tätä vain näkymän
               oma kohde vaihtui ja yläpalkki jäi entiseen (kävely ced6e9f: valittuna 26, linja kohteessa 32). */
            setOsoiteVirhe(null);
            if (ylapalkissa) void asetaYlakohde(r.kohde_id, false); else setKohde(r.kohde_id);
          } else if (r && !r.aktiivinen)
            setOsoiteVirhe(t("sel.linja_poistettu", { tunnus: osoiteKohde }));
          // Linja on olemassa ja tässä kohteessa, mutta ei listassa (haku rajaa listaa): haku tyhjenee, lista latautuu ja linja avautuu.
          else if (r) { setOsoiteVirhe(null); if (haku.trim()) setHaku(""); }
          else setOsoiteVirhe(t("sel.linjaa_ei_loydy", { tunnus: osoiteKohde }));
        } catch (e) {
          // Toni 31.10.2026 klo 16.20 kohta 2: «ei ole tässä instanssissa» ja «kohteessa, johon ei näkyvyyttä» erikseen.
          const v = e instanceof ApiError ? e.virhe : "";
          setOsoiteVirhe(v === "ei_loydy" ? t("sel.linjaa_ei_instanssissa", { tunnus: osoiteKohde })
            : v === "linja_ei_nakyvissa" ? t("sel.linja_ei_nakyvissa", { tunnus: osoiteKohde, kohde: String((e as ApiError).lisa?.kohde ?? "?") })
            : t("sel.linjaa_ei_loydy", { tunnus: osoiteKohde }));
        }
      })();
    }
    else
      setOsoiteVirhe(t("sel.monta_osumaa", { tunnus: osoiteKohde, n: osumat.length }));
  }, [osoiteKohde, linjat, haku]);
  // Uusi haku on uusi kysymys: edellisen osoitteen virhe ei koske sitä.
  useEffect(() => { setOsoiteVirhe(null); }, [haku]);

  /* T471 (videon 3 havainto 10.10.2026): Hubin «N linjaa» avasi Selailun, jossa oikea puoli oli tyhjä («valitse
     linja»). Kun osoitteessa on kohde mutta ei linjaa eikä muuta näkymää, avataan kerran «Kaikki linjat» sen kohteen
     hitseillä. Linjalista ja sen luku (data-rivit) pysyvät ennallaan. */
  const kaikkiAvattu = useRef(false);
  useEffect(() => {
    if (kaikkiAvattu.current || !linjat || osoiteKohde || kohde === "") return;
    if (alkuHaku.get("kohde") === null || alkuHaku.get("nayta")) return;
    kaikkiAvattu.current = true;
    void avaaKaikki();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linjat, osoiteKohde, kohde]);

  // Päätös 99: hitsilista on muokkaus- ja poistonappien lähde — vain viimeisimmän valinnan vastaus kirjoittaa sen.
  const uusiHaku = useViimeisin();
  async function avaaLinja(l: Linja) {
    const tuore = uusiHaku();
    setKaikki(false);
    setPunchNakyma(false);
    setValittu(l);
    setOsoiteVirhe(null);
    // Linjan avaaminen menee osoitteeseen: se on identiteetti.
    asetaOsoiteKohde?.(l.tunnus);
    setHitsit(null); setKattavuus(null);
    try { const h = await api.hitsit(l.id, 500, undefined, suunVaihe); if (tuore()) setHitsit(h); }
    catch { if (tuore()) setHitsit([]); }
    try { const ka = await api.ndtKattavuus(l.id); if (tuore()) setKattavuus(ka); }
    catch { if (tuore()) setKattavuus([]); }
  }

  async function avaaKaikki() {
    setKaikki(true); setVahvistettavatNakyma(false);
    setPunchNakyma(false);
    setValittu(null);
    setOsoiteVirhe(null);
    // "Kaikki linjat" on tila, ei identiteetti -- pois osoitteesta.
    asetaOsoiteKohde?.(null);
    setMuokattava(null);
    setKattavuus(null);
    setHitsit(null);
    const tuore = uusiHaku();
    try { const h = await api.hitsit(undefined, 500, kohde === "" ? undefined : kohde, suunVaihe); if (tuore()) setHitsit(h); }
    catch { if (tuore()) setHitsit([]); }
  }

  useEffect(() => {
    setVtValitut((v) => new Set([...v].filter((id) => hitsit?.some((h) => h.id === id && vtValittavissa(h)))));
  }, [hitsit]);
  // Toni 16.10.2026 klo 05.20: «VT ✓» tallentaa taas yhdellä painalluksella — VT ei tuota pöytäkirjaa, tarkastaja on kirjaaja.
  async function vtHyvaksy(h: Hitsi) {
    setVtKaynnissa(true); setVtIlmoitus(null);
    try {
      const v = await api.kirjaaVt({ rivit: [{ hitsi_id: h.id, tulos: "hyvaksytty" }] });
      setVtIlmoitus(v.ohitetut.length ? t("vt.ohitettu", { n: v.ohitetut.length }) : t("vt.kirjattu_yksi", { nro: h.hitsinumero }));
      await paivitaHitsit();
    } catch (e) { setVtIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("vt.virhe")); }
    finally { setVtKaynnissa(false); }
  }
  // Suodattimen vaihto lataa listan uudelleen (ensimmäinen piirto ohitetaan; avaaLinja/avaaKaikki lataavat itse).
  const suunEnsimmainen = useRef(true);
  useEffect(() => {
    if (suunEnsimmainen.current) { suunEnsimmainen.current = false; return; }
    void paivitaHitsit();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- vain suodattimen vaihto
  }, [vainSuunnitellut]);
  async function paivitaHitsit() {
    if (vahvistettavatNakyma) { try { setHitsit(await api.hitsitVahvistettavat(kohde || undefined)); } catch { /* */ } }
    else if (kaikki) { try { setHitsit(await api.hitsit(undefined, 500, kohde === "" ? undefined : kohde, suunVaihe)); } catch { /* */ } }
    else if (valittu) await avaaLinja(valittu);
  }
  /* 194 (päätös 62 kohta 5): «Odottaa vahvistusta» on tila kuten «Kaikki linjat» — joukko on palvelimen
     `vain_vahvistettavat=1`, sama lauseke kuin Hubin luvussa. */
  async function avaaVahvistettavat() {
    setVahvistettavatNakyma(true); setPunchNakyma(false); setKaikki(false); setValittu(null); setMuokattava(null);
    setOsoiteVirhe(null); asetaOsoiteKohde?.(null);
    const tuore = uusiHaku();
    try { const h = await api.hitsitVahvistettavat(kohde || undefined); if (!tuore()) return; setHitsit(h); setVirhe(null); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("sel.vahvistettavat_virhe")); }
  }
  useEffect(() => { if (vahvistettavatNakyma) void avaaVahvistettavat(); /* eslint-disable-line */ }, [vahvistettavatNakyma, kohde]);


  return (
    <>
      <header className="head">
        <div className="head__rivi"><h1>{t(vainVahvistettavat ? "valikko.odottaa_vahvistusta" : "valikko.selailu")}</h1></div>
      </header>
      <LatausTila l={klKohteet} nimi="lataus.kohteet" />

      {/* Ohje ylös (Toni 24.9.2026). Lähde: /api/linjat, /api/hitsit (+ PATCH korjattavissa), poistot, tuonnit ja punakynä — ks. Selailu.tsx:n oikeusliput. */}
      <Ohje
        kuka={<>{t("sel.ohje_kuka")}</>}
        luvut={<>{t("sel.ohje_luvut")}</>} />
    {/* T651 (Toni 23.10.2026 klo 12.15): viisi toimintoa yhdellä napparivillä — ennen viisi täysleveää riviä omine Avaa-nappeineen
        ennen hakukenttää. Napit vain oikeuden haltijalle kuten ennen (samat liput). */}
    <Napparivi nimi="selailu" toiminnot={[
      ...(saaTuodaExcelin ? [{ avain: "muokkaus", nimi: t("muu.hlm.otsikko"), sisalto: () => <HitsauslokiMuokkaus upotettu kohde_id={kohde === "" ? undefined : kohde} /> }] : []),
      ...(saaTuodaLinjat ? [{ avain: "linjat", nimi: t("sel.linjat_otsikko"), sisalto: () => (
        <PohjaTuontiPaneeli upotettu
          otsikko={t("sel.linjat_otsikko")}
          selite={t("sel.linjat_selite")}
          pohjaUrl={api.linjapohjaUrl()}
          esto={kohde === "" ? t("sel.linjat_esto") : null}
          tuo={(sisalto, tiedosto, vahvista, paivita) => api.tuoLinjat(Number(kohde), sisalto, tiedosto, vahvista, paivita)}
          paivitysTeksti={t("sel.linjat_paivitys")}
          valmis={() => void lataaLinjat(haku, kohde)} />) }] : []),
      // Päätös 85 vaihe 1: isometrien tuonti ja lista ihmiselle — sama joukko kuin linjojen kirjaus (tuonnin portti palvelimella).
      ...(saaKirjata ? [{ avain: "isometrit", nimi: t("muu.iso.otsikko"), sisalto: () => <IsometriTuonti upotettu kohde={kohde} paakayttaja={kayttaja.roolit.includes("paakayttaja")} /> }] : []),
      ...(saaTuodaNdt ? [{ avain: "ndt", nimi: t("sel.ndt_otsikko"), sisalto: () => (
        <PohjaTuontiPaneeli upotettu
          otsikko={t("sel.ndt_otsikko")}
          selite={t("sel.ndt_selite")}
          pohjaUrl={api.ndtPohjaUrl()}
          esto={kohde === "" ? t("sel.ndt_esto") : null}
          tuo={(sisalto, tiedosto, vahvista) => api.tuoNdtVaatimukset(Number(kohde), sisalto, tiedosto, vahvista)} />) }] : []),
      // T652 (Toni 23.10.2026 klo 19.35 «b»): linjojen PED-luokat asiakirjasta — samat oikeudet kuin NDT-vaatimuksilla.
      ...(saaTuodaNdt ? [{ avain: "ped", nimi: t("sel.ped_otsikko"), sisalto: () => (
        <PohjaTuontiPaneeli upotettu
          otsikko={t("sel.ped_otsikko")}
          selite={t("sel.ped_selite")}
          pohjaUrl={api.pedPohjaUrl()}
          esto={kohde === "" ? t("sel.ped_esto") : null}
          tuo={(sisalto, tiedosto, vahvista) => api.tuoPedLuokat(Number(kohde), sisalto, tiedosto, vahvista)} />) }] : []),
      ...(saaTuodaWps ? [{ avain: "wps", nimi: t("sel.wps_otsikko"), sisalto: () => (
        <PohjaTuontiPaneeli upotettu
          otsikko={t("sel.wps_otsikko")}
          selite={t("sel.wps_selite")}
          pohjaUrl={api.wpsPohjaUrl()}
          tuo={(sisalto, tiedosto, vahvista) => api.tuoWps(sisalto, tiedosto, vahvista)} />) }] : []),
    ]} />
    <section className="panel">
      {!vainVahvistettavat && <div className="selailu__suodattimet">
        <input className="selailu__haku" value={haku} placeholder={t("sel.haku_placeholder")}
               autoComplete="off" onChange={(e) => setHaku(e.target.value)} />
        {!ylapalkissa && <label className="dim">{t("kuittaus.kohde")}{" "}
          <select value={kohde}
                  onChange={(e) => setKohde(e.target.value ? Number(e.target.value) : "")}>
            <option value="">{t("kohde.kaikki")}</option>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
          </select>
        </label>}
      </div>}
      {virhe && <p className="huom" role="alert">{virhe}</p>}
      {/* Jaetun linkin virhe on OMA rivinsa: sen syy on eri kuin
          latausvirheen, ja lukija on eri ihminen -- se joka sai
          linkin eika se joka avasi nakyman. */}
      {osoiteVirhe && <p className="huom huom--vika" role="alert">{osoiteVirhe}</p>}

      <div className="selailu">
        {!vainVahvistettavat && <div className="selailu__linjat">
          <button className={"selailu__linja selailu__linja--kaikki" + (kaikki ? " selailu__linja--auki" : "")}
                  onClick={() => void avaaKaikki()}>
            <span className="lyh">▤ {t("sel.kaikki_linjat")}</span>
          </button>
          <button className={"selailu__linja selailu__linja--kaikki" + (punchNakyma ? " selailu__linja--auki" : "")}
                  onClick={() => { setPunchNakyma(true); setKaikki(false); setValittu(null); setVahvistettavatNakyma(false);
                                   setMuokattava(null); setOsoiteVirhe(null);
                                   asetaOsoiteKohde?.(null); }}>
            <span className="lyh">◆ {t("sel.kaikki_punchit")}</span>
          </button>
          <button className={"selailu__linja selailu__linja--kaikki" + (vahvistettavatNakyma ? " selailu__linja--auki" : "")}
                  data-selailu-vahvistettavat="" onClick={() => void avaaVahvistettavat()}>
            <span className="lyh">✎ {t("sel.odottaa_vahvistusta")}</span>
          </button>
          {linjat === null && <p className="dim">{t("ltar.ladataan")}</p>}
          {linjat?.length === 0 && <p className="dim">{t("sel.ei_linjoja")}</p>}
          {/* Suodatuksen koko palvelimelta (`yhteensa`), ei listan
              pituus: pyyntö on rajattu 1000:een. Hubin "N linjaa"
              verrataan tähän (`data-rivit`, kavely.mjs). */}
          {linjat && (
            <p className="dim" style={{ margin: "0 0 6px" }}>
              <span data-rivit={linjat[0]?.yhteensa ?? 0} data-joukko="linjat">
                {linjat[0]?.yhteensa ?? 0}
              </span>{haku.trim() && linjat[0]?.kaikkiaan != null ? <span data-linjahaku-kaikkiaan={linjat[0].kaikkiaan}> / {linjat[0].kaikkiaan}</span> : null}{" "}{t("sel.linjaa")}
              {(linjat[0]?.yhteensa ?? 0) > linjat.length ? ` · ${t("sel.naytetaan", { n: linjat.length })}` : ""}
              <br /><span data-linjalista-selite="">{t(linjat.some((l) => l.suunniteltuja_hitseja != null) ? "sel.maara_selite" : "sel.maara_selite_rivit")}</span>
            </p>
          )}
          {linjat?.map((l) => (
            <button key={l.id}
              className={"selailu__linja" + (valittu?.id === l.id ? " selailu__linja--auki" : "")}
              onClick={() => void avaaLinja(l)}>
              <span className="lyh">{l.tunnus}</span>
              {/* T677 F (Toni 27.10.2026 klo 09.08): «20 / 20» ei kertonut mitä luvut ovat — selite listan yläpuolella ja vihjeenä. */}
              <span className="selailu__maara mono"
                    title={l.suunniteltuja_hitseja != null ? t("sel.maara_title", { n: l.hitseja, m: l.suunniteltuja_hitseja }) : t("sel.maara_title_rivit", { n: l.hitseja })}>
                {l.hitseja}{l.suunniteltuja_hitseja != null ? ` / ${l.suunniteltuja_hitseja}` : ""}
              </span>
              {(l.avoimia_puncheja ?? 0) > 0 && (
                <span className="selailu__punch" title={t("sel.avoimia_puncheja")}>{l.avoimia_puncheja} {t("sel.punch_lyh")}</span>
              )}
              {!l.isometri_olemassa && <span className="dim selailu__lippu">{t("nippu.ei_isometria")}</span>}
            </button>
          ))}
        </div>}

        <div className="selailu__hitsit">
          {!valittu && !kaikki && !punchNakyma && !vahvistettavatNakyma && <p className="dim">{t("sel.valitse_ohje")}</p>}
          {/* Toni 17.10.2026 klo 07.40 kohta 2: sama punch-oikeus kuin linjan näkymässä (tarkastaja mukaan). */}
          {punchNakyma && <PunchSelailu kohde={kohde} saaKirjata={saaKirjataPunch} saaPoistaaLiitteen={saaPoistaaLiitteen}
                                        vainAvoimet={vainAvoimet} asetaVainAvoimet={setVainAvoimet}
                                        kayttaja={kayttaja} hoitaja={punchHoitaja} asetaHoitaja={setPunchHoitaja} />}
          {(valittu || kaikki || vahvistettavatNakyma) && (
            <>
              <h3 data-rivit={vahvistettavatNakyma ? (hitsit?.length ?? 0) : undefined} data-joukko={vahvistettavatNakyma ? "vahvistettavat" : undefined}>{vahvistettavatNakyma
                ? t("sel.otsikko_vahvistettavat", { n: hitsit?.length ?? 0 })
                : kaikki
                ? (hitsit && (hitsit[0]?.yhteensa ?? 0) > hitsit.length
                    ? <span data-rajattu={hitsit.length} data-yhteensa={hitsit[0]?.yhteensa}>{t("sel.otsikko_kaikki_rajattu", { n: hitsit.length, yht: hitsit[0]?.yhteensa ?? 0 })}</span>
                    : t("sel.otsikko_kaikki", { n: hitsit?.length ?? 0 }))
                : <><span className="lyh" data-linja={valittu!.tunnus}>{valittu!.tunnus}</span> · {t("sel.n_hitsia", { n: valittu!.hitseja })}</>}</h3>
              {!vahvistettavatNakyma && (
                <label className="dim" style={{ display: "inline-flex", gap: 6, alignItems: "center", minHeight: 44, cursor: "pointer" }}
                       data-suodatin-suunnitellut="">
                  <input type="checkbox" checked={vainSuunnitellut} onChange={(e) => setVainSuunnitellut(e.target.checked)} />
                  {t("sel.vain_suunnitellut")}
                </label>
              )}
              {/* T584: peruuntuneen kohteen linja — selvä merkintä; palvelin kieltäytyy kirjauksista (409 kohde_peruuntunut). */}
              {!kaikki && valittu && kohteet.find((x) => x.id === valittu.kohde_id)?.tila === "peruuntunut" && (
                <p className="huom huom--vika" role="alert" data-linja-peruuntunut={valittu.kohde_id}>
                  {t("sel.kohde_peruuntunut", { kohde: kohteet.find((x) => x.id === valittu.kohde_id)?.tunnus ?? "" })}</p>)}
              {/* 227 (T453c): koosteen linkki voi osua jo tyhjentyneeseen jonoon — sanotaan se, ei virhesivua. */}
              {vahvistettavatNakyma && hitsit?.length === 0 && <p className="dim" data-vahvistettavat-tyhja="">{t("sel.ei_vahvistettavia")}</p>}

              {!kaikki && valittu && (
                <div style={{ margin: "2px 0 10px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {saaMuokataLinjaa && (
                    <button className="nappi nappi--kevyt" onClick={() => setLinjaMuokkausAuki((v) => !v)}>
                      {linjaMuokkausAuki ? t("sel.sulje_linjatiedot") : t("sel.muokkaa_linjaa")}
                    </button>
                  )}
                </div>
              )}
              {!kaikki && valittu && eiTehdaAuki && (
                <HitsiaEiTehda
                  linjaId={valittu.id}
                  linjaTunnus={valittu.tunnus}
                  sulje={() => setEiTehdaAuki(false)}
                  valmis={async () => { setEiTehdaAuki(false); setHitsit(await api.hitsit(valittu.id, 500, undefined, suunVaihe)); }}
                />
              )}
              {!kaikki && valittu && saaMuokataLinjaa && linjaMuokkausAuki && (
                <LinjanMuokkaus linja={valittu} valmis={() => { setLinjaMuokkausAuki(false); void lataaLinjat(haku, kohde); }} />
              )}

              {/*
                Tyhjä tila sanotaan ääneen. Aiemmin koko lohko katosi kun
                ndt_vaatimus-taulussa ei ollut riviä tälle (kohde, PED-luokka)
                -parille — ja katoava lohko näyttää samalta kuin "ei
                huomautettavaa". Vaatimusta ei ole kirjattu on eri asia kuin
                vaatimus on täytetty. (PAATOKSET §Hiljainen ohitus.)
              */}
              {!kaikki && valittu && kattavuus && kattavuus.length === 0 && (
                <div className="kattavuus">
                  <span className="kattavuus__otsikko">{t("sel.ndt_kattavuus")}</span>
                  <span className="dim" >
                    {valittu.ped_luokka
                      ? t("sel.ei_ndt_vaatimusta", { luokka: valittu.ped_luokka })
                      : t("sel.ei_ped_luokkaa")}
                  </span>
                </div>
              )}

              {!kaikki && kattavuus && kattavuus.length > 0 && (
                <div className="kattavuus">
                  <span className="kattavuus__otsikko">{t("sel.ndt_kattavuus")}</span>
                  {kattavuus.map((k) => {
                    const vaadittuKpl = Math.ceil(k.yhteensa * k.vaadittu_osuus);
                    const vaje = k.tarkastettu < vaadittuKpl;
                    // T677 G (Toni 27.10.2026 klo 09.08): linjalla ei ole hitsejä → «–» ilman ✓:tä. Tyhjä ei ole valmis; 0/0 ✓ näytti valmiilta.
                    if (k.yhteensa === 0) return (
                      <span key={k.menetelma} className="kattavuus__k dim" data-ndt-tyhja={k.menetelma} title={t("sel.ndt_ei_hitseja")}>
                        {k.menetelma} –
                      </span>
                    );
                    return (
                      <span key={k.menetelma}
                            className={"kattavuus__k " + (vaje ? "kattavuus__k--vaje" : "kattavuus__k--ok")}
                            title={t("sel.vaadittu_title", { p: Math.round(k.vaadittu_osuus * 100), kpl: vaadittuKpl, yht: k.yhteensa })}>
                        {k.menetelma} {k.tarkastettu}/{vaadittuKpl}
                        {vaje ? " ⚠" : " ✓"}
                      </span>
                    );
                  })}
                </div>
              )}

              {!kaikki && kattavuus && kattavuus.some((k) => k.tarkastettu < Math.ceil(k.yhteensa * k.vaadittu_osuus)) && (
                <div style={{ margin: "0 0 10px", padding: "8px 12px",
                  background: "var(--accent-soft, #fdecef)", borderLeft: "3px solid var(--puna)", fontSize: 13 }}>
                  ⚠ <b>{t("sel.ndt_vaje")}</b> — {t("sel.ndt_vaje_teksti")}
                </div>
              )}

              {/* Osiot työn järjestyksessä (Toni 25.10.2026): punchit → laippaliitokset → linjatarkastus → painekoe.
                  Punchit kuitataan ennen linjatarkastusta, ja koeponnistus tehdään sen jälkeen. Kävely lukee järjestyksen
                  [data-linjasivu-osio]-määritteistä. */}
              {!kaikki && valittu && (
                /* Päätös 89 (6.10.2026): key = linja. Linjan vaihto purkaa lomakkeen ja rakentaa uuden, joten edellisen linjan
                   myöhästynyt vastaus päätyy purettuun komponenttiin eikä täytä uuden linjan kenttiä (kävely 89 pakottaa tämän). */
                /* Avaimet ovat sisaruksille yksilölliset (6.10.2026, kävely 99): sama key={valittu.id} viidellä sisaruksella rikkoi
                   sovituksen — vanhat punch-listat jäivät DOMiin («Ladataan…» kolmesti). */
                <div data-linjasivu-osio="punch">
                  <Punchit key={`punch-${valittu.id}`} linjaId={valittu.id} saaKirjata={saaKirjataPunch} saaPoistaaLiitteen={saaPoistaaLiitteen}
                    kayttaja={kayttaja}
                    onMuutos={() => void lataaLinjat(haku, kohde)} />
                </div>
              )}

              {!kaikki && valittu && laippaliitokset && (
                <div data-linjasivu-osio="laippa">
                  <Laippaliitokset key={`laippa-${valittu.id}`} linjaId={valittu.id} saaKiristaa={saaKiristaa} saaKasitella={saaKasitellaKiristyksen}
                                   toisenPuolesta={kiristaaToisenPuolesta} omaHenkilo={kayttaja.resurssi_id} />
                </div>
              )}

              {!kaikki && valittu && (
                <div data-linjasivu-osio="linjatarkastus" style={{ marginTop: 12, borderTop: "1px solid var(--viiva)", paddingTop: 12 }}>
                  {/* Toni 19.10.2026 klo 11.50: tarkastuskuvat isometrin yläpuolelle, iso painike, tabletilla kamera. */}
                  <LinjatarkastusKuvat key={`ltk-${valittu.id}`} linjaId={valittu.id}
                    saaMuokata={saaKirjata && !kayttaja.linjatarkastus_sharepointissa} muuttui={() => setKuvaversio((v) => v + 1)} />
                  <div style={{ marginTop: 12 }} />
                  {/* Päätös 85: virheiden merkintä isometriin (nasta = punch) linjatarkastuksen yhteydessä. */}
                  <IsometriMerkinnat key={`iso-${valittu.id}`} linjaId={valittu.id} saaKirjata={saaKirjata} onMuutos={() => void lataaLinjat(haku, kohde)} />
                  <div style={{ marginTop: 12 }} />
                  {/* 235 (T520): kuka ilmoitti linjan valmiiksi linjatarkastukseen — eri asia kuin johdettu valmius. */}
                  <Valmiusilmoitus key={`vi-${valittu.id}`} linjaId={valittu.id}
                    saaIlmoittaa={kayttaja.roolit.some((r) => r === "paakayttaja" || r === "suunnittelija" || r === "tyonjohtaja")} />
                  <LinjaTarkastus key={`lt-${valittu.id}`} linjaId={valittu.id} saaMuokata={saaKirjata} sharepointissa={kayttaja.linjatarkastus_sharepointissa} oletusTarkastaja={kayttaja.nimi ?? undefined} kuvatErikseen versio={kuvaversio} />
                  <LinjanPuutteet key={`pu-${valittu.id}`} linjaId={valittu.id} saaKorvata={saaKorvata(kayttaja)} />
                </div>
              )}

              {/* Painekoe on linjan koe ja kuuluu linjan sivulle (D1, 18.9.2026) — ei tekstikenttänä linjan tiedoissa.
                  Linjatarkastuksen jälkeen, koska koeponnistus tehdään tarkastetulle linjalle (Toni 25.10.2026). */}
              {!kaikki && valittu && (
                <div data-linjasivu-osio="painekoe" style={{ marginTop: 12, borderTop: "1px solid var(--viiva)", paddingTop: 12 }}>
                  <Painekoe key={`painekoe-${valittu.id}`} linjaId={valittu.id} saaKirjata={saaKirjata}
                            saaPoistaaLiitteen={saaPoistaaLiitteen} />
                </div>
              )}

              {hitsit === null && <p className="dim">{t("ltar.ladataan")}</p>}
              {hitsit?.length === 0 && <p className="dim">{t("sel.ei_hitseja")}</p>}
              {hitsit && hitsit.length > 0 && saaKirjataVt && (
                <div className="vt-tyokalut" data-vt-tyokalut="">
                  <button type="button" className="nappi nappi--kevyt" data-vt-valitse-kaikki=""
                          onClick={() => setVtValitut(new Set((jarjestetyt ?? hitsit).filter(vtValittavissa).map((h) => h.id)))}>
                    {t("vt.valitse_kaikki")}</button>
                  {vtValitut.size > 0 && <button type="button" className="nappi nappi--kevyt" onClick={() => setVtValitut(new Set())}>{t("vt.tyhjenna")}</button>}
                  <span className="dim" data-vt-valittu={vtValitut.size}>{t("vt.valittu", { n: vtValitut.size })}</span>
                  <button type="button" className="nappi" disabled={vtValitut.size === 0} onClick={() => setVtIkkuna(true)} data-vt-kirjaa-valituille="">
                    {t("vt.kirjaa_valituille", { n: vtValitut.size })}</button>
                  {vtIlmoitus && <span className="dim" role="status" data-vt-ilmoitus="">{vtIlmoitus}</span>}
                </div>
              )}
              {vtIkkuna && hitsit && (
                <VtJoukkoIkkuna kayttaja={kayttaja} hitsit={hitsit.filter((h) => vtValitut.has(h.id))}
                  sulje={() => setVtIkkuna(false)}
                  valmis={() => { setVtIkkuna(false); setVtValitut(new Set()); void paivitaHitsit(); }} />
              )}
              {/* 158: linjatason merkintä — numero, jota ei ole rivinä (mitattu pekilossa 21.9.2026: useimmilla linjoilla puuttuvaa
                  numeroa ei ole rivinä lainkaan); rivin «Ei toteudu» vaatii rivin. T677 F (Toni 27.10.2026 klo 10.10): nappi siirretty
                  linjan otsikosta hitsitaulukon työkaluriville nimellä «+ Numero, jota ei hitsata», jottei se sekoitu hitsikohtaiseen
                  «Ei toteudu» -merkintään. */}
              {!kaikki && valittu && saaEsittaa && hitsit !== null && (
                <div className="vt-tyokalut" data-hitsi-tyokalurivi="">
                  <button className="nappi nappi--kevyt" data-numero-ei-hitsata="" style={{ minHeight: 44 }}
                          title={t("sel.ei_tehda_title")}
                          onClick={() => setEiTehdaAuki(true)}>
                    {t("sel.numero_ei_hitsata")}
                  </button>
                </div>
              )}
              {hitsit && hitsit.length > 0 && (
                /* T677 A (Toni 27.10.2026 klo 10.10): hitsilista vierii omassa ikkunassaan, vaakapalkki aina näkyvissä. */
                <div className="taulu-ikkuna" data-taulu-ikkuna="hitsit">
                  <table className="selailu__taulu">
                    <thead>
                      <tr>
                        {saaKirjataVt && <th title={t("vt.sarake_title")}>VT</th>}
                        {(kaikki || vahvistettavatNakyma) && <th className="selailu__th" onClick={() => asetaJarjestys("linja")}>{t("nippu.linja")}{nuoli("linja")}</th>}
                        <th className="selailu__th" onClick={() => asetaJarjestys("media")}>{t("kuittaus.rajain_media")}{nuoli("media")}</th>
                        {/* «Isometri»-sarake poistettu (Toni 13.10.2026): se näytti linjatunnuksen 5. osan eli putkiluokan (2eb7a69), ja isometri on linja itse. */}
                        <th className="selailu__th" onClick={() => asetaJarjestys("lehti")}>{t("hl.lehti")}{nuoli("lehti")}</th>
                        <th className="selailu__th" onClick={() => asetaJarjestys("revisio")}>Rev{nuoli("revisio")}</th>
                        <th className="selailu__th" onClick={() => asetaJarjestys("nro")}>{t("sel.nro")}{nuoli("nro")}</th>
                        <th className="selailu__th" onClick={() => asetaJarjestys("tyyppi")}>{t("sel.laji")}{nuoli("tyyppi")}</th>
                        <th className="selailu__th" onClick={() => asetaJarjestys("vaihe")}>{t("kuittaus.vaihe")}{nuoli("vaihe")}</th>
                        <th className="selailu__th" onClick={() => asetaJarjestys("paiva")}>{t("hl.paiva")}{nuoli("paiva")}</th>
                        <th className="selailu__th" onClick={() => asetaJarjestys("dn")}>DN{nuoli("dn")}</th>
                        <th>{t("sel.osat_sulatus")}</th>
                        <th title={t("sel.sulatus_taydennetty")}>{t("sel.sul_lyh")}</th>
                        <th className="selailu__th" onClick={() => asetaJarjestys("hitsaaja")}>{t("hl.hitsaaja")}{nuoli("hitsaaja")}</th>
                        <th title={t("sel.punakyna_title")}>{t("sel.punakyna")}</th>
                        {tarraNakyy && <th title={t("sel.tarra_title")}>{t("hl.tarra")}</th>}
                        <th>NDT</th>
                        {saaEsittaa && <th className="selailu__toim" data-toiminnot-otsikko="" />}
                      </tr>
                    </thead>
                    <tbody>
                      {jarjestaEmonAlle(jarjestetyt ?? hitsit, jarjestys === null).map((h) => (
                        <Fragment key={h.id}>
                          <tr data-hitsi={h.id} data-emo={h.emo_hitsi_id ?? undefined} data-hitsinumero={h.hitsinumero} data-lehti={h.piirustuslehti ?? ""}>
                            {saaKirjataVt && <VtSolu h={h} valittu={vtValitut.has(h.id)} kaynnissa={vtKaynnissa}
                              vaihda={() => setVtValitut((v) => { const n = new Set(v); if (n.has(h.id)) n.delete(h.id); else n.add(h.id); return n; })}
                              hyvaksy={() => void vtHyvaksy(h)}
                              hylkaa={() => { setVtPikaTulos("hylatty"); setVtHylkays(vtHylkays === h.id && vtPikaTulos === "hylatty" ? null : h.id); }}
                              ndt={() => setNdtAuki(ndtAuki === h.id ? null : h.id)} />}
                            {(kaikki || vahvistettavatNakyma) && <td className="mono"><LinjaLinkki tunnus={h.linja} /></td>}
                            <td className="mono">{(h.linja ?? "").split("-")[2] ?? ""}</td>
                            <td className="mono">{h.piirustuslehti}</td>
                            <td className="mono">{h.revisio ?? ""}</td>
                            {/* ⛔ NUMEROSOLUSSA ON VAIN NUMERO (Toni 21.9.2026):
                                nappi peitti sen. Tunniste on se jolla rivi
                                etsitään paperilta, ja se luetaan ennen kuin
                                mihinkään painetaan. Merkinnät ja napit ovat
                                omissa sarakkeissaan rivin lopussa. */}
                            <td className={"mono" + (h.emo_hitsi_id ? " selailu__alinumero" : "")}>{h.emo_hitsi_id ? <span className="dim">└ </span> : null}{naytaHitsinumero(h.hitsinumero)}
                              {h.numero_tarkastettava && <> <Tilasana laji="varoitus" title={t("sel.numero_tarkastettava_ohje")}><span data-numero-tarkastettava="">{t("sel.numero_tarkastettava")}</span></Tilasana></>}</td>
                            <td>{h.hitsityyppi ? hitsityyppiNimi(h.hitsityyppi) : <Tilasana laji="ei_mitattu">{t("sel.ei_valittu")}</Tilasana>}</td>
                            <td title={selite("hitsi_vaihe", h.vaihe)}>{nayttonimi("hitsi_vaihe", h.vaihe)}</td>
                            <td className="mono">
                              {naytaPvm(h.paiva) ?? "—"}{h.paiva && h.paiva_epatarkka ? " ~" : ""}
                            </td>
                            <td className="mono">{h.dn ?? ""}</td>
                            <td className="selailu__osat">
                              {h.osat.map((o, i) => (
                                <span key={i}>
                                  {i > 0 && " + "}
                                  {o.osalaji || "?"}
                                  {o.sulatus ? ` (${o.sulatus})` : ""}
                                </span>
                              ))}
                            </td>
                            <td>{h.sulatus_taydennetty ? "✓" : <span className="dim">–</span>}</td>
                            <td className="mono">{h.juuri_hitsaaja_koodi ?? ""}</td>
                            {/* 152/155: punakynän tila omana sarakkeenaan.
                                Sana on mukana, ei pelkkä väri — ruudun on oltava
                                luettava mustavalkoisena. Toteutumaton hitsi EI
                                katoa listalta: rivi on päätöksen tallenne. */}
                            <td className="selailu__punakyna" data-punakyna-tila={h.vahvistettava ? "odottaa" : h.lisays_hylatty ? "hylatty" : h.ei_toteudu ? "ei_toteudu" : h.alinumero ? "hyvaksytty" : ""}>
                              {/* 194 (päätös 62): lisähitsi näkyy emon alla muodossa 21.1 ja tila on «hyväksymättä» kunnes
                                  vahvistettu; hylätty jää riville. Tilasana avaa historian (peruutus asuu siellä). */}
                              {(h.alinumero || h.lisays_hylatty || h.ei_toteudu || h.vahvistettava || h.emo_hitsi_id) && (
                                /* Toni 26.10.2026 klo 06.15: tilasana näyttää napilta — tekstinä sitä ei löytänyt kukaan (peruutus asuu historiassa). */
                                <button type="button" className="nappi nappi--kevyt selailu__punakyna-tila" data-punakyna-historia={h.id}
                                        style={{ border: "1px solid var(--viiva-vahva)", padding: "4px 10px", minHeight: 32 }}
                                        onClick={() => setPunakynaHistoria(h)} title={t("sel.punakyna_historia")}>
                                  {h.vahvistettava?.laji === "lisays" && <Tilasana laji="varoitus">{t("sel.t_lisa_hyvaksymatta")}</Tilasana>}
                                  {h.vahvistettava?.laji === "ei_toteudu" && <Tilasana laji="varoitus">{t("sel.t_ei_toteudu_odottaa")}</Tilasana>}
                                  {h.vahvistettava?.laji === "poisto" && <Tilasana laji="varoitus">{t("sel.t_poisto_odottaa")}</Tilasana>}
                                  {!h.vahvistettava && h.lisays_hylatty && !h.lisays_hyvaksytty && <Tilasana laji="vika">{t("sel.t_lisa_hylatty")}</Tilasana>}
                                  {!h.vahvistettava && (h.alinumero || h.emo_hitsi_id) && h.lisays_hyvaksytty && <Tilasana laji="kunnossa">{t("sel.t_lisa_hyvaksytty")}</Tilasana>}
                                  {!h.vahvistettava && h.ei_toteudu && (h.ei_toteudu_luovutuskelpoinen
                                    ? <Tilasana laji="ei_mitattu">{t("sel.t_ei_toteudu_hyvaksytty")}</Tilasana>
                                    : <Tilasana laji="varoitus">{t("sel.t_ei_toteudu_tyonjohto")}</Tilasana>)}
                                  {/* T677 K: ulkopuolinen hyväksyjä merkinnän vieressä («Tilaajan edustaja: <nimi>»). */}
                                  {!h.vahvistettava && h.punakyna_ulkopuolinen && (
                                    <span className="dim" data-punakyna-ulkopuolinen={h.id}
                                          title={h.punakyna_ulkopuolinen.kirjaaja ? t("hyv.kirjaaja", { nimi: h.punakyna_ulkopuolinen.kirjaaja }) : undefined}>
                                      {" "}{hyvaksyjanEsitys(HYVAKSYJAN_ASEMAT.find((a) => a.arvo === h.punakyna_ulkopuolinen!.asema)?.nimi ?? null, h.punakyna_ulkopuolinen.nimi)}
                                    </span>
                                  )}
                                </button>
                              )}
                              {/* Toni 26.10.2026 klo 06.15: «ei toteudu» -merkinnän viereen oma «Peru»-nappi; peruste ja asema kysytään
                                  historiaikkunassa (peruutus on uusi rivi, alkuperäinen kirjaus jää). */}
                              {saaPunakyna && !h.vahvistettava && h.ei_toteudu && (
                                <button type="button" className="nappi nappi--kevyt" data-punakyna-peru-avaa={h.id}
                                        style={{ marginLeft: 6 }} onClick={() => setPunakynaHistoria(h)}>{t("sel.peru_ei_toteudu")}</button>
                              )}
                            </td>
                            {tarraNakyy && <td className="mono">{h.tarra ?? ""}
                              {/* 239 (T566): korvattu tarra on historiaa, ei kadonnut — määrä riville. */}
                              {(h.tarra_korvattuja ?? 0) > 0 && <span className="dim" data-tarra-korvattuja={h.tarra_korvattuja}
                                title={t("sel.tarra_korvattuja_title")}> · {t("sel.tarra_korvattuja", { n: h.tarra_korvattuja ?? 0 })}</span>}</td>}
                            <td className="selailu__ndt">
                              {vtHylatty(h) && <><Tilasana laji="vika" title={vtHylatty(h)!.huomiot ?? undefined}>
                                <span data-vt-hylatty={h.id}>{t("vt.hylatty_tila")}{vtHylatty(h)!.huomiot ? `: ${vtHylatty(h)!.huomiot!.replace(/^VT hylätty: /, "")}` : ""}</span></Tilasana>{" "}</>}
                              {h.tarkastukset.map((t) => (
                                <span key={t.id} className={`ndt ndt--${t.tulos}`}
                                      title={`${t.menetelma}: ${nayttonimiPieni("ndt_tulos", t.tulos)}`}>{t.menetelma}
                                  {/* 233 (T562): pöytäkirjan liite numeron kautta — sama 📎 kaikilla saman numeron hitseillä. */}
                                  {(t.poytakirja_liitteita ?? 0) > 0 && <span data-ndt-poytakirja={t.raportti ?? ""} title={t.raportti ?? ""}> 📎</span>}</span>
                              ))}
                              {/* T525: hylätyn RT/UT/MT/PT:n syy rivillä kuten hylätyn VT:n (huomiot «UT hylätty: <vika> — <kuvaus>»). */}
                              {h.tarkastukset.filter((x) => x.tulos === "hylatty" && x.menetelma !== "VT" && x.huomiot).map((x) => (
                                <span key={`hyl-${x.id}`}>{" "}<Tilasana laji="vika"><span data-ndt-hylatty={x.id}>{x.huomiot}</span></Tilasana></span>
                              ))}
                              {/* T675 (Toni 31.10.2026 klo 11.00 kohta 5): hitsin huomio riville, liitostieto erotettuna. */}
                              {hitsinHuomio(h.huomiot) && (
                                <div className="dim" data-hitsi-huomio={h.id}
                                     style={{ fontSize: "0.9em", marginTop: 2, whiteSpace: "normal", overflowWrap: "anywhere", maxWidth: "40ch" }}>
                                  {t("sel.hitsin_huomio")}: {hitsinHuomio(h.huomiot)}</div>
                              )}
                              {/* 7.10.2026 (Norjan erä): kentältä liitetyt kuvat näkyvät linjan hitsirivillä. */}
                              {(h.kuvia ?? 0) > 0 && (
                                <button className="tekstilinkki" data-selailu-hitsi-kuvat={h.id} style={{ marginLeft: 6 }}
                                        onClick={() => setKuvaHitsi(kuvaHitsi === h.id ? null : h.id)}>{t("sel.kuvat_n", { n: h.kuvia ?? 0 })}</button>
                              )}
                            </td>
                            {saaEsittaa && (
                              <td className="selailu__toim">
                                {/* Hyväksyjä voi olla ulkopuolinen valvoja, joka on
                                    rooliltaan lukija eikä saa muokata mitään. Siksi
                                    sarake näkyy myös hänelle, ja jokainen nappi on
                                    oman ehtonsa takana. */}
                                {/* 194 (päätös 62): punakynä näkyy VAIN rivillä jolla on vahvistamaton muutos, ja nappi
                                    kertoo mitä vahvistetaan. Historia (hyväksynnän peruutus) avautuu tilasanasta. */}
                                {saaVahvistaaPoiston && h.vahvistettava?.laji === "poisto" && (
                                  <button className="nappi nappi--kevyt" data-poisto-ratkaise={h.vahvistettava.esitys_id ?? ""}
                                          onClick={() => setPoisto(h)}>
                                    {t("sel.ratkaise_poisto", { nro: h.hitsinumero })}
                                  </button>
                                )}
                                {saaPunakyna && h.vahvistettava && h.vahvistettava.laji !== "poisto" && (
                                  <button className="nappi nappi--kevyt" data-punakyna-vahvista={h.vahvistettava.laji}
                                          title={t("sel.vahvista_title", { nro: h.hitsinumero })}
                                          onClick={() => setPunakyna(h)}>
                                    {h.vahvistettava.laji === "lisays" ? t("sel.vahvista_lisa", { nro: h.hitsinumero }) : t("sel.vahvista_ei_toteudu", { nro: h.hitsinumero })}
                                  </button>
                                )}
                                {saaMuokataHitseja && h.korjattavissa && (
                                  <button className="nappi nappi--kevyt"
                                          onClick={() => setMuokattava(muokattava === h.id ? null : h.id)}>
                                    {muokattava === h.id ? t("hl.sulje") : t("hl.korjaa")}
                                  </button>
                                )}
                                {/* Punakynän kaksi tekoa rivin lopussa, pois
                                    numerosolun päältä. «+ hitsi» eikä «+ väliin»:
                                    nappi kertoo mitä se tekee, ei mihin kohtaan. */}
                                {saaEsittaa && (
                                  <button className="nappi nappi--kevyt" data-lisaa-hitsi={h.id}
                                          title={t("sel.lisaa_hitsi_title", { nro: h.hitsinumero })}
                                          onClick={() => setValiin(h)}>{t("sel.lisaa_hitsi")}</button>
                                )}
                                {/* ⛔ «Ei toteudu» VAIN suunnitellulle. Hitsattu sauma
                                    on tehtyä työtä — sen purku on korjaus eikä
                                    punakynä (Toni 21.9.2026). Palvelin torjuu myös,
                                    eikä napin puuttuminen ole se portti. */}
                                {saaEsittaa && h.vaihe === "suunniteltu" && !h.ei_toteudu && !h.vahvistettava && (
                                  <button className="nappi nappi--kevyt"
                                          title={t("sel.ei_toteudu_title", { nro: h.hitsinumero })}
                                          onClick={() => setEiToteudu(h)}>{t("sel.ei_toteudu")}</button>
                                )}
                                {/* 62 kohta 4: poisto vain riville jota ei ole hyväksytty eikä tuotu — palvelimen
                                    päätös (poistettavissa), ei selaimen. Muille «Ei toteudu». 218: nappi tekee esityksen. */}
                                {/* 10.10.2026: esityksen saa tehdä myös kenttätekijä (218: palvelin sallii; Testaus-ohjeen tehtävä
                                    «Pyydä poistoa» ei toiminut, koska nappi vaati muokkausroolin). */}
                                {saaEsittaa && h.poistettavissa && !h.vahvistettava && (
                                  <button className="nappi nappi--kevyt" data-hitsi-poista=""
                                          onClick={() => setPoisto(h)}>{t("sel.pyyda_poistoa")}</button>
                                )}
                              </td>
                            )}
                          </tr>
                          {saaKirjataVt && vtHylkays === h.id && (
                            <VtHylkays key={vtPikaTulos} h={h} tulos={vtPikaTulos} sarakkeita={(kaikki ? 16 : 15) - (tarraNakyy ? 0 : 1)}
                              valmis={() => { setVtHylkays(null); setVtIlmoitus(t("vt.kirjattu_yksi", { nro: h.hitsinumero })); void paivitaHitsit(); }} peru={() => setVtHylkays(null)} />
                          )}
                          {saaKirjataVt && ndtAuki === h.id && (
                            <NdtRivi h={h} sarakkeita={(kaikki ? 16 : 15) - (tarraNakyy ? 0 : 1)} oletusTarkastaja={kayttaja.nimi ?? ""}
                              valmis={() => { setNdtAuki(null); void paivitaHitsit(); }} peru={() => setNdtAuki(null)} />
                          )}
                          {kuvaHitsi === h.id && (
                            <tr className="tbl__laajennus">
                              <td colSpan={(kaikki ? 15 : 14) - (tarraNakyy ? 0 : 1)}>
                                <Liitteet kohdeTaulu="hitsi" kohdeId={h.id} saaMuokata={!!h.kuva_lisattavissa} saaPoistaa={false}
                                          otsikko={t("sel.kuvat_hitsi", { nro: h.hitsinumero })} muuttui={() => paivitaHitsit()} />
                              </td>
                            </tr>
                          )}
                          {saaMuokataHitseja && h.korjattavissa && muokattava === h.id && (
                            <HitsinMuokkaus h={h} sarakkeita={(kaikki ? 15 : 14) - (tarraNakyy ? 0 : 1)}
                              valmis={() => {
                                setMuokattava(null);
                                void paivitaHitsit();
                                void lataaLinjat(haku, kohde);
                              }}
                              paivita={() => void paivitaHitsit()}
                              peruuta={() => setMuokattava(null)} />
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>

      {/* 152: punakynän hyväksymisikkuna. Renderöidään juuritasolla, jotta
          se ei jää taulukon vierityksen sisään. */}
      {valiin && valittu && (
        <HitsiValiinIkkuna
          linja={valittu}
          edellinen={valiin}
          sulje={() => setValiin(null)}
          valmis={async () => { setValiin(null); if (valittu) await avaaLinja(valittu); }}
        />
      )}
      {eiToteudu && (
        <EiToteuduIkkuna
          hitsi={eiToteudu}
          sulje={() => setEiToteudu(null)}
          valmis={async () => { setEiToteudu(null); if (valittu) await avaaLinja(valittu); }}
        />
      )}
      {poisto && (
        <PoistoIkkuna
          hitsi={poisto} saaRatkaista={saaVahvistaaPoiston}
          sulje={() => setPoisto(null)}
          valmis={async () => {
            setPoisto(null);
            if (valittu) await avaaLinja(valittu);
            if (kaikki || vahvistettavatNakyma) await paivitaHitsit();
            void lataaLinjat(haku, kohde);    // linjan hitsimäärä päivittyy
          }}
        />
      )}
      {punakynaHistoria && (
        <PunakynaIkkuna
          hitsi={punakynaHistoria} vainHistoria
          tyonjohto={kayttaja.roolit.some((r) => PUNAKYNAN_TYONJOHTO.includes(r))}
          sulje={() => setPunakynaHistoria(null)}
          valmis={async () => { if (valittu) await avaaLinja(valittu); if (kaikki || vahvistettavatNakyma) await paivitaHitsit(); }}
        />
      )}
      {punakyna && (
        <PunakynaIkkuna
          hitsi={punakyna}
          tyonjohto={kayttaja.roolit.some((r) => PUNAKYNAN_TYONJOHTO.includes(r))}
          sulje={() => setPunakyna(null)}
          valmis={async () => { if (valittu) await avaaLinja(valittu); if (kaikki || vahvistettavatNakyma) await paivitaHitsit(); }}
        />
      )}
    </>
  );
}

/* ── linjan tietojen muokkaus ─────────────────────────────── */

function LinjanMuokkaus({ linja, valmis }: { linja: Linja; valmis: () => void }) {
  const [media, setMedia] = useState(linja.media ?? "");
  const [dn, setDn] = useState(linja.dn_nimellinen != null ? String(linja.dn_nimellinen) : "");
  const [putkiluokka, setPutkiluokka] = useState(linja.putkiluokka ?? "");
  const [ped, setPed] = useState(linja.ped_luokka ?? "");
  const [suun, setSuun] = useState(linja.suunniteltuja_hitseja != null ? String(linja.suunniteltuja_hitseja) : "");
  const [isom, setIsom] = useState(linja.isometri_olemassa);
  const [tallentaa, setTallentaa] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);

  async function tallenna() {
    setTallentaa(true); setVirhe(null);
    try {
      await api.muokkaaLinja(linja.id, {
        media: media.trim(), putkiluokka: putkiluokka.trim(), ped_luokka: ped.trim(),
        dn_nimellinen: dn.trim() ? Number(dn.replace(",", ".")) : null,
        suunniteltuja_hitseja: suun.trim() ? Number(suun) : null,
        isometri_olemassa: isom,
      });
      valmis();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : e instanceof Error ? e.message : t("ltar.tallennus_virhe")); }
    finally { setTallentaa(false); }
  }

  const k: CSSProperties = { display: "flex", flexDirection: "column", fontSize: 11 };
  return (
    <div style={{ background: "var(--pelti)", border: "1px solid var(--viiva)", padding: 12, margin: "0 0 12px" }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={k}>{t("kuittaus.rajain_media")}<input value={media} onChange={(e) => setMedia(e.target.value)} style={{ width: 80 }} /></label>
        <label style={k}>DN<input value={dn} onChange={(e) => setDn(e.target.value)} inputMode="numeric" style={{ width: 60 }} /></label>
        <label style={k}>{t("sel.putkiluokka")}<input value={putkiluokka} onChange={(e) => setPutkiluokka(e.target.value)} style={{ width: 90 }} /></label>
        <label style={k}>{t("sel.ped_luokka")}<input value={ped} onChange={(e) => setPed(e.target.value)} style={{ width: 70 }} /></label>
        <label style={k}>{t("sel.suunn_hitseja")}<input value={suun} onChange={(e) => setSuun(e.target.value)} inputMode="numeric" style={{ width: 70 }} /></label>
        <label style={{ ...k, flexDirection: "row", alignItems: "center", gap: 5 }}>
          <input type="checkbox" checked={isom} onChange={(e) => setIsom(e.target.checked)} /> {t("sel.isometri_olemassa")}</label>
        <button className="nappi nappi--paa" disabled={tallentaa} onClick={tallenna}>{tallentaa ? "…" : t("sel.tallenna_linja")}</button>
      </div>
      {virhe && <p className="huom huom--vika" role="alert" style={{ marginTop: 6 }}>{virhe}</p>}
    </div>
  );
}

/* ── hitsin muokkaus (kirjausvirheen korjaus) ─────────────── */


function HitsinMuokkaus({
  h, sarakkeita, valmis, paivita, peruuta,
}: {
  h: Hitsi; sarakkeita: number;
  valmis: () => void; paivita: () => void; peruuta: () => void;
}) {
  const osa = (j: number) => h.osat.find((o) => o.jarjestys === j);
  const [numero, setNumero] = useState(h.hitsinumero);
  const [tyyppi, setTyyppi] = useState<Hitsityyppi | "">(h.hitsityyppi ?? "");
  const [paiva, setPaiva] = useState(h.paiva ?? "");
  const [n1, setN1] = useState(osa(1)?.osalaji ?? "");
  const [s1, setS1] = useState(osa(1)?.sulatus ?? "");
  const [n2, setN2] = useState(osa(2)?.osalaji ?? "");
  const [s2, setS2] = useState(osa(2)?.sulatus ?? "");
  const [mat, setMat] = useState(osa(1)?.materiaali ?? "");
  const [juuri, setJuuri] = useState(h.juuri_hitsaaja_koodi ?? "");
  // Asentaja (T331): ennen 130:tä lomake kirjoitti asentajan täyttöhitsaajaksi, joten vanha arvo luetaan asentajaksi.
  const [asentaja, setAsentaja] = useState(h.asentaja_koodi ?? h.tayton_hitsaaja_koodi ?? "");
  const [syyLaji, setSyyLaji] = useState<import("./api").KorjauksenSyyLaji | "">("");
  const [syyTeksti, setSyyTeksti] = useState("");
  const [tallennetaan, setTallennetaan] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  async function paivitaRaportti(id: number, raportti: string, vanha: string | null) {
    if ((raportti.trim() || "") === (vanha ?? "")) return;
    try { await api.paivitaTarkastus(id, { raportti: raportti.trim() }); paivita(); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ltar.tallennus_virhe")); }
  }
  async function poistaTarkastus(id: number) {
    try { await api.poistaTarkastus(id); paivita(); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("liite.poisto_virhe")); }
  }

  async function tallenna() {
    setTallennetaan(true); setVirhe(null);
    try {
      await api.muokkaaHitsi(h.id, {
        hitsinumero: numero.trim(), hitsityyppi: tyyppi || undefined,
        ...(paiva && { paiva }),
        juuri_hitsaaja_koodi: juuri.trim(),
        asentaja_koodi: asentaja.trim(),
        osat: [
          { jarjestys: 1, osalaji: n1.trim() || undefined, materiaali: mat.trim() || undefined, sulatus: s1.trim() || undefined },
          { jarjestys: 2, osalaji: n2.trim() || undefined, materiaali: mat.trim() || undefined, sulatus: s2.trim() || undefined },
        ],
        ...(korjauksenSyy(syyLaji, syyTeksti) && { korjaus_syy: korjauksenSyy(syyLaji, syyTeksti) }),
      });
      valmis();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ltar.tallennus_virhe"));
    } finally { setTallennetaan(false); }
  }

  return (
    <tr className="selailu__muokkaus">
      <td colSpan={sarakkeita}>
        <div className="allokointi">
          <label>{t("sel.nro")}<input value={numero} size={5} onChange={(e) => setNumero(e.target.value)} /></label>
          <label>{t("sel.laji")}
            <select value={tyyppi} onChange={(e) => setTyyppi(e.target.value as Hitsityyppi | "")}>
              {h.hitsityyppi == null && <option value="">{t("sel.ei_valittu")}</option>}
              <option value="A">{t("hl.laji.A")}</option>
              <option value="E">{t("hl.laji.E")}</option>
              <option value="R">{t("hl.laji.R")}</option>
              <option value="S">{t("sel.laji_s")}</option>
            </select>
          </label>
          <label>{t("hl.paiva")}<input type="date" value={paiva} onChange={(e) => setPaiva(e.target.value)} /></label>
          <label>{t("sel.osa_nimike", { n: 1 })}<input value={n1} size={7} onChange={(e) => setN1(e.target.value)} /></label>
          <label>{t("sel.osa_sulatus", { n: 1 })}<input value={s1} size={7} onChange={(e) => setS1(e.target.value)} /></label>
          <label>{t("sel.osa_nimike", { n: 2 })}<input value={n2} size={7} onChange={(e) => setN2(e.target.value)} /></label>
          <label>{t("sel.osa_sulatus", { n: 2 })}<input value={s2} size={7} onChange={(e) => setS2(e.target.value)} /></label>
          <label>{t("hl.materiaali")}<input value={mat} size={7} onChange={(e) => setMat(e.target.value)} /></label>
          <label>{t("hl.hitsaaja")}<input value={juuri} size={5} onChange={(e) => setJuuri(e.target.value)} /></label>
          <label>{t("hl.asentaja")}<input value={asentaja} size={5} onChange={(e) => setAsentaja(e.target.value)} /></label>
          <KorjauksenSyyValinta laji={syyLaji} teksti={syyTeksti} asetaLaji={setSyyLaji} asetaTeksti={setSyyTeksti} />
          <button className="nappi nappi--paa" disabled={tallennetaan} onClick={() => void tallenna()}>
            {tallennetaan ? t("pu.tallennetaan") : t("ltar.tallenna")}
          </button>
          <button className="nappi nappi--kevyt" disabled={tallennetaan} onClick={peruuta}>{t("hl.peruuta")}</button>
        </div>

        <div className="ndt-hallinta">
          <h4>{t("sel.ndt_ja_painekoe")}</h4>
          {h.tarkastukset.length === 0 && <span className="dim">{t("sel.ei_tarkastuksia")}</span>}
          {h.tarkastukset.map((tark) => (
            <div key={tark.id} className="ndt-kohde">
              <div className="ndt-rivi">
                <span className={`ndt ndt--${tark.tulos}`}>{tark.menetelma}</span>
                {/* 11.10.2026 (pääChatin toimeksianto Tonin välittämänä): NDT-tulosta ei muokata Korjaa-lomakkeessa — VT kirjataan
                    hitsilistan VT-sarakkeesta, ja virheellinen tarkastus poistetaan ja kirjataan uudelleen. */}
                <span data-ndt-tulos={tark.tulos}>{tark.tulos === "hyvaksytty" ? t("sel.ndt_hyvaksytty") : tark.tulos === "hylatty" ? t("sel.ndt_hylatty") : t("sel.ndt_kesken")}</span>
                <span className="mono dim">
                  {naytaPvm(tark.paiva) ?? ""}{tark.tarkastaja ? ` · ${tark.tarkastaja}` : ""}
                </span>
                <label className="dim">{t("iv.poytakirja")}
                  <input defaultValue={tark.raportti ?? ""} size={10} placeholder={t("sel.ei_numeroa")}
                         data-raportti={tark.raportti ?? ""}
                         onBlur={(e) => void paivitaRaportti(tark.id, e.target.value, tark.raportti ?? null)} />
                </label>
                <button className="nappi nappi--kevyt" onClick={() => void poistaTarkastus(tark.id)}>{t("liite.poista")}</button>
              </div>
                 {/* Tarkastuspöytäkirja liitteenä juuri tähän tarkastukseen.
                     `saaMuokata` on tässä vakio eikä aukko: koko HitsinMuokkaus
                     renderöityy vain `saaMuokataHitseja`-portin sisällä
                     (pk + suun), joka on sama lista kuin POST/DELETE
                     /api/liitteet ja DELETE /api/tarkastukset/:id. */}
                 <Liitteet kohdeTaulu="tarkastus" kohdeId={tark.id} saaMuokata otsikko={t("sel.poytakirja")} />
                 {/* 233 (T562): pöytäkirjanumeron liitteet — yhteiset kaikille saman numeron hitseille; poisto arkistoi. */}
                 {tark.poytakirja_id && <Liitteet kohdeTaulu="poytakirja" kohdeId={tark.poytakirja_id} saaMuokata
                   otsikko={t("ptk.numeron_liitteet", { nro: tark.raportti ?? "" })} lisaaTeksti={t("ptk.lisaa_sivu")} poistaTeksti={t("ptk.arkistoi")} />}
            </div>
          ))}
          <NdtLomake h={h} oletusTarkastaja="" valmis={paivita} />
        </div>

        {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      </td>
    </tr>
  );
}

/* ── Punakynä (152) ──────────────────────────────────────────
   Kentällä tehdyn muutoksen jälkikäteinen hyväksyntä.

   ⛔ Perustelu on pakollinen, ja nappi on estetty ilman sitä. Se ei ole
   kohteliaisuus vaan koko tallenteen arvo: hyväksyntä ilman perustetta
   on leima, eikä leima kestä kysymystä «miksi» kahden vuoden päästä.

   Historia näytetään kokonaan, myös perutut rivit — append-only taulu
   ei kadota mitään, eikä näkymä saa kadottaa sen puolesta. */
function PunakynaIkkuna({ hitsi, tyonjohto, sulje, valmis, vainHistoria = false }: {
  hitsi: Hitsi; tyonjohto: boolean; sulje: () => void; valmis: () => Promise<void>; vainHistoria?: boolean;
}) {
  const [historia, setHistoria] = useState<Punakyna[] | null>(null);
  const [peruste, setPeruste] = useState("");
  /* 194 (päätös 62): laji tulee MUUTOKSESTA, ei valikosta — «Mitä hyväksytään» poistui. */
  const v = hitsi.vahvistettava ?? null;
  const laji: "lisays" | "ei_toteudu" = v?.laji === "ei_toteudu" ? "ei_toteudu" : "lisays";
  const [hylkays, setHylkays] = useState(false);
  /* Toiminnon jälkeen ikkuna jää auki historiana: käyttäjä näkee mitä kirjattiin (asema, taso) ja sulkee itse. */
  const [tehty, setTehty] = useState(false);
  const vainHistoriaNyt = vainHistoria || tehty;
  const [virhe, setVirhe] = useState<string | null>(null);
  const [kesken, setKesken] = useState(false);
  /* 160: asema valitaan hyväksymishetkellä, ja tarjolla on vain se mitä
     kirjaajalla on: kenttätyönjohto roolilla, ulkopuoliset oikeudella (ikkuna
     avautuu vain oikeudella). Työnjohdolle oletus on kenttätyönjohto = heikompi
     väite; ulkopuoliselle EI oletusta — vahvempi väite valitaan nimenomaisesti. */
  const asemat = HYVAKSYJAN_ASEMAT.filter((a) => a.ulkopuolinen || tyonjohto);
  const [asema, setAsema] = useState<HyvaksyjanAsema | "">(tyonjohto ? "kenttatyonjohto" : "");
  /* T677 K (Toni 27.10.2026 klo 09.15): ulkopuolisella tahon edustajalla ei ole tunnusta — nimi pakollinen, viite vapaaehtoinen.
     T677 J: puuttuva peruste tai nimi kentän kohdalle punaisena, ei harmaata kieltoa eikä hiljaa harmaata nappia. */
  const ulkopuolinen = !!HYVAKSYJAN_ASEMAT.find((a) => a.arvo === asema)?.ulkopuolinen;
  const [ulkNimi, setUlkNimi] = useState("");
  const [ulkViite, setUlkViite] = useState("");
  const [ulkTiedosto, setUlkTiedosto] = useState<File | null>(null);   // T679
  const kv = useKenttavirheet<"peruste" | "nimi">();
  /** T679: ulkopuolisen hyväksynnän liite tallentuu juuri luodun punakynärivin liitteeksi. Hyväksyntä on jo tallessa, joten
   *  epäonnistunut lähetys sanotaan ääneen eikä peru hyväksyntää. */
  const liitaTiedosto = async (punakynaId: number) => {
    if (!ulkopuolinen || !ulkTiedosto) return;
    try {
      await api.lisaaLiite({ kohde_taulu: "punakyna", kohde_id: punakynaId, nimi: ulkTiedosto.name, sisalto: await lueBase64(ulkTiedosto) });
      setUlkTiedosto(null);
    } catch (e) {
      setVirhe(t("hyv.v.liite_epaonnistui", { syy: e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e) }));
    }
  };
  /** Tarkistus ennen kutsua; false = virheet näkyvissä kenttien kohdalla. */
  const tarkista = (perusteMin = 3) => kv.tarkista([
    ["peruste", peruste.trim().length < perusteMin && t("sel.v.kirjoita_peruste")],
    ["nimi", ulkopuolinen && ulkNimi.trim().length < 2 && t("hyv.v.nimi_puuttuu")],
  ]);
  const ulk = () => (ulkopuolinen ? { ulkopuolinen_nimi: ulkNimi.trim(), ...(ulkViite.trim() ? { ulkopuolinen_viite: ulkViite.trim() } : {}) } : {});
  const palvelinvirhe = (e: unknown, oletus: string) => {
    if (e instanceof ApiError && e.virhe === "hyvaksyjan_nimi_puuttuu") { kv.aseta("nimi", t("hyv.v.nimi_puuttuu")); return; }
    setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : oletus);
  };
  /** T677 L: esitys on avoin, kun sitä ei ole hylätty eikä myöhempi saman lajin hyväksyntä ole vahvistanut sitä. */
  const esitysAvoin = (p: Punakyna) => (p.laji === "esitys" || p.laji === "ei_toteudu_esitys") && !p.perunut_id
    && !(historia ?? []).some((x) => x.id > p.id && !x.perunut_id && x.laji === (p.laji === "esitys" ? "lisays" : "ei_toteudu"));

  const hae = useCallback(async () => {
    try { setHistoria(await api.punakynaHistoria(hitsi.id)); setVirhe(null); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("sel.historia_virhe")); }
  }, [hitsi.id]);
  useEffect(() => { if (historia === null) void hae(); }, [historia, hae]);

  const LAJI: Record<string, string> = {
    esitys: t("sel.pk_esitys"), ei_toteudu_esitys: t("sel.pk_ei_toteudu_esitys"),
    lisays: t("sel.pk_lisays"), ei_toteudu: t("sel.pk_ei_toteudu"), peruutus: t("sel.pk_peruutus"), hylkays: t("sel.pk_hylkays"),
  };

  return (
    <div className="ikkuna" data-punakyna-ikkuna={vainHistoriaNyt ? "historia" : "vahvistus"} data-punakyna-tehty={tehty ? "1" : undefined}>
      <div className="ikkuna__sisalto">
        <h2>{vainHistoriaNyt
          ? (tehty
            ? (laji === "lisays" ? t("sel.kirjattu_lisa", { nro: hitsi.hitsinumero }) : t("sel.kirjattu_ei_toteudu", { nro: hitsi.hitsinumero }))
            : t("sel.punakyna_historia"))
          : laji === "lisays" ? t("sel.vahvista_lisa", { nro: hitsi.hitsinumero }) : t("sel.vahvista_ei_toteudu", { nro: hitsi.hitsinumero })}</h2>
        {!vainHistoriaNyt && v && (
          <p data-punakyna-muutos="">
            <strong>{laji === "lisays" ? t("sel.lisahitsi_nro", { nro: hitsi.hitsinumero }) : t("sel.hitsi_ei_toteudu", { nro: hitsi.hitsinumero })}</strong>
            {hitsi.emo_hitsinumero ? <> · {t("sel.emo")} {hitsi.emo_hitsinumero}</> : null}
            {v.esitys_id === null
              ? <><br /><span className="dim">{t("sel.alinumero_ilman_esitysta")}</span></>
              : <><br /><span className="dim">{v.kirjaaja ?? "?"} · {naytaPvm(v.hetki)}</span><br />{v.peruste}</>}
          </p>
        )}

        {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

        {/* T677 L (Toni 27.10.2026 klo 09.20): historia sarakkeina — tapahtuma, aika, tekijä ja taho, tila, merkitys, perustelu ja
            toiminto omissa sarakkeissaan; napit oikeina nappeina oikeassa reunassa ja vain riveillä, joilla teko on sallittu.
            Ennen «Hylkää» näkyi jo hylätyllä esityksellä (ehto luki avausta edeltävän tilan, ei historiaa), ja rivi sanoi
            «peruttu» myös hylätystä esityksestä. */}
        {historia && historia.length > 0 && (
          <div className="taulu-ikkuna" data-taulu-ikkuna="punakyna-historia" style={{ maxHeight: "45vh", minHeight: 0 }}>
          <table className="tbl" style={{ fontSize: 13 }} data-punakyna-historia="">
            <thead><tr>
              <th>{t("sel.pkh.tapahtuma")}</th><th>{t("sel.pkh.aika")}</th><th>{t("sel.pkh.tekija")}</th><th>{t("sel.pkh.tila")}</th>
              <th>{t("sel.pkh.merkitys")}</th><th>{t("sel.pkh.perustelu")}</th><th className="sarake-toiminnot">{t("sel.pkh.toiminnot")}</th>
            </tr></thead>
            <tbody>
            {historia.map((p) => {
              const asemaNimi = HYVAKSYJAN_ASEMAT.find((a) => a.arvo === p.hyvaksyjan_asema)?.nimi ?? p.hyvaksyjan_asema ?? null;
              const hyvaksynta = p.laji === "lisays" || p.laji === "ei_toteudu";
              const esitys = p.laji === "esitys" || p.laji === "ei_toteudu_esitys";
              const avoin = esitysAvoin(p);
              const saaPeruuttaa = !p.perunut_id && hyvaksynta;
              return (
              <tr key={p.id} data-punakyna-rivi={p.id} data-punakyna-laji={p.laji}>
                <td><strong>{LAJI[p.laji] ?? p.laji}</strong></td>
                <td className="mono" style={{ whiteSpace: "nowrap" }}>{naytaPvm(p.hyvaksytty)}</td>
                <td data-punakyna-tekija={p.id}>
                  {p.ulkopuolinen_nimi
                    ? <><strong>{hyvaksyjanEsitys(asemaNimi, p.ulkopuolinen_nimi)}</strong>
                        <div className="dim">{t("hyv.kirjaaja", { nimi: p.hyvaksyja })}{p.ulkopuolinen_viite ? ` · ${p.ulkopuolinen_viite}` : ""}</div>
                        {/* T679: ulkopuolisen hyväksynnän todiste rivin liitteenä; työnjohto voi lisätä sen myös jälkikäteen. */}
                        <div data-punakyna-liitteet={p.id}>
                          <Liitteet kohdeTaulu="punakyna" kohdeId={p.id} saaMuokata={tyonjohto} saaPoistaa={false}
                                    otsikko={t("hyv.liitteet")} muuttui={hae} />
                        </div></>
                    : <>{p.hyvaksyja}{asemaNimi && <span className="dim"> ({asemaNimi})</span>}</>}
                </td>
                <td data-punakyna-tila={p.id}>
                  {p.perunut_laji === "hylkays" ? <Tilasana laji="vika">{t("sel.pkh.hylatty")}</Tilasana>
                    : p.perunut_id ? <Tilasana laji="ei_mitattu">{t("sel.peruttu")}</Tilasana>
                    : esitys ? (avoin ? <Tilasana laji="varoitus">{t("sel.pkh.odottaa")}</Tilasana> : <span className="dim">{t("sel.pkh.kasitelty")}</span>)
                    : hyvaksynta ? <Tilasana laji="kunnossa">{t("sel.pkh.voimassa")}</Tilasana>
                    : <span className="dim">–</span>}
                </td>
                <td>
                  {p.taso === "nimetty" ? <Tilasana laji="kunnossa">{t("sel.asetettu_hyvaksyja")}</Tilasana>
                    : p.taso === "tyonjohto" ? <Tilasana laji="varoitus">{t("sel.tyonjohto_ei_riita")}</Tilasana>
                    : <span className="dim">–</span>}
                </td>
                <td>{p.peruste}</td>
                <td className="sarake-toiminnot" style={{ whiteSpace: "nowrap" }}>
                  {/* Toni 26.10.2026 klo 06.15: esitystä ei peruta (kanta torjuu) vaan hylätään — vain AVOIMELLE esitykselle «Hylkää».
                      Peru tämä vain voimassa olevalle hyväksynnälle (lisäys, ei toteudu). */}
                  {saaPeruuttaa && (
                    <button className="nappi" style={{ minHeight: 44 }} disabled={kesken}
                            data-punakyna-peru={p.id} onClick={() => void peru(p.id)}>{t("sel.peru_tama")}</button>
                  )}
                  {avoin && (
                    <button className="nappi" style={{ minHeight: 44 }} disabled={kesken}
                            data-punakyna-hylkaa-esitys={p.id} onClick={() => void hylkaaEsitys(p.id)}>{t("pu.hylkaa")}</button>
                  )}
                </td>
              </tr>
              );
            })}
            </tbody>
          </table>
          </div>
        )}
        {historia?.length === 0 && (
          <p className="dim">{t("sel.ei_punakynaa")}</p>
        )}

        {/* Tonin havainto 11.10.2026 (demo 810-0011, hitsi 011): lomake näkyi myös hitsillä, jolla ei ole punakynää.
            Peruutuslomake vain kun historiassa on perumaton hyväksyntä; muuten pelkkä «Sulje». */}
        {vainHistoriaNyt && !(historia ?? []).some((p) => !p.perunut_id && p.laji !== "peruutus") ? (
          <div className="lomake lomake--tiivis" data-punakyna-ei-perottavaa="">
            <button className="nappi nappi--kevyt" onClick={sulje}>{t("hl.sulje")}</button>
          </div>
        ) : vainHistoriaNyt ? (
          <div className="lomake lomake--tiivis" data-punakyna-peruutuslomake="">
            <h3 style={{ flex: "1 1 100%", margin: "4px 0 0" }}>{t("sel.peru_hyvaksynta")}</h3>
            <label style={{ flex: "1 1 280px" }}>{t("sel.peruutuksen_perustelu")}
              <input value={peruste} {...kv.kentta("peruste")} data-punakyna-peruste=""
                     onChange={(e) => { setPeruste(e.target.value); if (e.target.value.trim().length >= 3) kv.poista("peruste"); }}
                     placeholder={t("sel.peruutus_placeholder")} />
              <Kenttavirhe {...kv.virhe("peruste")} />
            </label>
            <label data-punakyna-asema="">{t("sel.asemassa")}
              <select value={asema} disabled={kesken} onChange={(e) => setAsema(e.target.value as HyvaksyjanAsema | "")}>
                {!tyonjohto && <option value="">{t("sel.valitse_asema")}</option>}
                {asemat.map((a) => <option key={a.arvo} value={a.arvo}>{a.nimi}</option>)}
              </select>
            </label>
            <HyvaksyjanTaho ulkopuolinen={ulkopuolinen} nimi={ulkNimi} viite={ulkViite} poisKaytosta={kesken}
                            asetaNimi={(s) => { setUlkNimi(s); if (s.trim().length >= 2) kv.poista("nimi"); }} asetaViite={setUlkViite}
                            nimiKentta={kv.kentta("nimi")} nimiVirhe={kv.virhe("nimi")} tiedosto={ulkTiedosto} asetaTiedosto={setUlkTiedosto} />
            <button className="nappi nappi--kevyt" onClick={sulje}>{t("hl.sulje")}</button>
          </div>
        ) : (
        <div className="lomake lomake--tiivis">
          <label style={{ flex: "1 1 280px" }}>{hylkays ? t("sel.hylkayksen_peruste") : t("sel.vahvistuksen_perustelu")}
            <input value={peruste} {...kv.kentta("peruste")} data-punakyna-peruste=""
                   onChange={(e) => { setPeruste(e.target.value); if (e.target.value.trim().length >= 3) kv.poista("peruste"); }}
                   placeholder={hylkays ? t("sel.hylkays_placeholder") : t("sel.vahvistus_placeholder")} />
            <Kenttavirhe {...kv.virhe("peruste")} />
          </label>
          <label data-punakyna-asema="">{t("sel.hyvaksyn_asemassa")}
            <select value={asema} disabled={kesken}
                    onChange={(e) => setAsema(e.target.value as HyvaksyjanAsema | "")}>
              {!tyonjohto && <option value="">{t("sel.valitse_asema")}</option>}
              {asemat.map((a) => (
                <option key={a.arvo} value={a.arvo}>
                  {a.nimi}{" — "}{a.ulkopuolinen ? t("sel.kelpaa_luovutukseen") : t("sel.ei_riita_luovutukseen")}
                </option>
              ))}
            </select>
          </label>
          <HyvaksyjanTaho ulkopuolinen={ulkopuolinen} nimi={ulkNimi} viite={ulkViite} poisKaytosta={kesken}
                          asetaNimi={(s) => { setUlkNimi(s); if (s.trim().length >= 2) kv.poista("nimi"); }} asetaViite={setUlkViite}
                          nimiKentta={kv.kentta("nimi")} nimiVirhe={kv.virhe("nimi")} tiedosto={ulkTiedosto} asetaTiedosto={setUlkTiedosto} />
          {!hylkays && (
            <button className="nappi nappi--paa" data-punakyna-vahvista-nappi="" disabled={kesken || !asema}
                    onClick={() => void hyvaksy()}>{t("sel.vahvista")}</button>
          )}
          {!hylkays && (
            <button className="nappi nappi--kevyt" data-punakyna-hylkaa-nappi="" disabled={kesken || !v} onClick={() => setHylkays(true)}>{t("sel.hylkaa_ellipsi")}</button>
          )}
          {hylkays && (
            <button className="nappi nappi--paa" data-punakyna-hylkaa-vahvista="" disabled={kesken || !asema || !v?.esitys_id}
                    title={v?.esitys_id ? "" : t("sel.vanha_ei_hylata")}
                    onClick={() => void hylkaa()}>{t("pu.hylkaa")}</button>
          )}
          <button className="nappi nappi--kevyt" onClick={hylkays ? () => setHylkays(false) : sulje}>{hylkays ? t("hl.peruuta") : t("hl.sulje")}</button>
        </div>
        )}

        {/* Tonin havainto 11.10.2026: selitys oli seitsemän riviä. Kaksi lausetta näkyvissä, loput «Lue lisää» -kohdassa. */}
        <p className="ikkuna__vihje" data-punakyna-selite="">{t("sel.pk_vihje_lyhyt")}</p>
        <details className="ikkuna__vihje">
          <summary>{t("sel.lue_lisaa")}</summary>
          {t("sel.pk_vihje")}
          {" "}{t("sel.pk_vihje_tj")} <strong>{t("sel.pk_vihje_heti")}</strong>{t("sel.pk_vihje_loppu")}
        </details>
      </div>
    </div>
  );

  async function hyvaksy() {
    setVirhe(null);
    if (!tarkista()) return;
    setKesken(true);
    try {
      const r = await api.hyvaksyPunakyna(hitsi.id, laji, peruste.trim(), asema || undefined, ulk());
      await liitaTiedosto(r.id);
      setPeruste(""); setUlkNimi(""); setUlkViite(""); kv.tyhjenna(); setTehty(true); setHistoria(null); await hae(); await valmis();
    } catch (e) {
      palvelinvirhe(e, t("sel.vahvistus_virhe"));
    } finally { setKesken(false); }
  }
  async function hylkaa() {
    if (!v?.esitys_id) return;
    setVirhe(null);
    if (!tarkista()) return;
    setKesken(true);
    try {
      const r = await api.hylkaaPunakyna(v.esitys_id, peruste.trim(), asema || undefined, ulk());
      await liitaTiedosto(r.id);
      setPeruste(""); setUlkNimi(""); setUlkViite(""); kv.tyhjenna(); setTehty(true); setHylkays(false); setHistoria(null); await hae(); await valmis();
    } catch (e) {
      palvelinvirhe(e, t("sel.hylkays_virhe"));
    } finally { setKesken(false); }
  }

  async function hylkaaEsitys(id: number) {
    setVirhe(null);
    if (!tarkista()) return;
    setKesken(true);
    try {
      const r = await api.hylkaaPunakyna(id, peruste.trim(), asema || undefined, ulk());
      await liitaTiedosto(r.id);
      setPeruste(""); setUlkNimi(""); setUlkViite(""); kv.tyhjenna(); setHistoria(null); await hae(); await valmis();
    } catch (e) {
      palvelinvirhe(e, t("sel.hylkays_virhe"));
    } finally { setKesken(false); }
  }
  async function peru(id: number) {
    setVirhe(null);
    if (!tarkista()) return;
    setKesken(true);
    try {
      const r = await api.peruPunakyna(id, peruste.trim(), asema || undefined, ulk());
      await liitaTiedosto(r.id);
      setPeruste(""); setUlkNimi(""); setUlkViite(""); kv.tyhjenna(); setHistoria(null); await hae(); await valmis();
    } catch (e) {
      palvelinvirhe(e, t("sel.peruutus_virhe"));
    } finally { setKesken(false); }
  }
}

/** 194 (päätös 62): lisähitsi näkyy EMON ALLA (21 → 21.1, 21.2) kun käyttäjä ei ole valinnut lajittelua.
 *  Palvelin antaa uusin ensin; tässä alinumerot siirretään emonsa perään, muu järjestys säilyy. Emoton
 *  alinumero (vanha, pekilo) jää paikalleen. */
function jarjestaEmonAlle(rivit: Hitsi[], kaytossa: boolean): Hitsi[] {
  if (!kaytossa) return rivit;
  const idt = new Set(rivit.map((h) => h.id));
  const lapset = new Map<number, Hitsi[]>();
  for (const h of rivit) {
    if (h.emo_hitsi_id && idt.has(h.emo_hitsi_id)) { const l = lapset.get(h.emo_hitsi_id) ?? []; l.push(h); lapset.set(h.emo_hitsi_id, l); }
  }
  if (lapset.size === 0) return rivit;
  const nro = (x: string) => Number(x.replace(",", ".").split(".")[1] ?? "0");
  const tulos: Hitsi[] = [];
  for (const h of rivit) {
    if (h.emo_hitsi_id && idt.has(h.emo_hitsi_id)) continue;
    tulos.push(h);
    for (const l of (lapset.get(h.id) ?? []).sort((a, b) => nro(a.hitsinumero) - nro(b.hitsinumero))) tulos.push(l);
  }
  return tulos;
}

/* ── «+ hitsi väliin» (153) ──────────────────────────────────
   Kentällä lisätty hitsi kahden numeron väliin: 6 ja 7 väliin 6.1.

   ⛔ Perustelu on pakollinen, ja palvelin torjuu ilman sitä
   (`punakyna_peruste_puuttuu`). Nappi on estetty samasta syystä — mutta
   palvelin on se joka päättää, ei selain: kaksi ehtoa samaan sääntöön
   erkanee, ja selaimen puoli on se joka ohitetaan.

   Ehdotuksen antaa PALVELIN (`/api/hitsit/seuraava-alinumero`), koska
   «seuraava vapaa» on yksi määritelmä eikä selaimen tulkinta ladatusta
   listasta. Kanta on silti tuomari: uniikki linja+lehti+numero torjuu
   törmäyksen jos kaksi kirjaa samaan aikaan. */
function HitsiValiinIkkuna({ linja, edellinen, sulje, valmis }: {
  linja: Linja; edellinen: Hitsi; sulje: () => void; valmis: () => Promise<void>;
}) {
  const [numero, setNumero] = useState<string | null>(null);
  const [peruste, setPeruste] = useState("");
  // 66 (Toni 5.10.2026 «Kyllä»): lisähitsi joko hitsattiin nyt tai se on vasta suunniteltu (kirjataan kun tehdään).
  const [tila, setTila] = useState<"hitsattu" | "suunniteltu">("hitsattu");
  const [virhe, setVirhe] = useState<string | null>(null);
  const [kesken, setKesken] = useState(false);

  useEffect(() => {
    api.seuraavaAlinumero(linja.id, edellinen.hitsinumero, edellinen.piirustuslehti ?? undefined)
      .then((r) => setNumero(r.ehdotus))
      .catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("sel.ehdotus_virhe")));
  }, [linja.id, edellinen.hitsinumero, edellinen.piirustuslehti]);

  /* Numero lihavoituna lauseen keskellä: lause on yksi käännösavain, ja numeron paikka luetaan siitä. */
  const [valiinAlku, valiinLoppu = ""] = t("sel.valiin_selite", {
    nro: "\u0001",
    lehti: edellinen.piirustuslehti ? " " + t("sel.lehdella", { lehti: edellinen.piirustuslehti }) : "",
  }).split("\u0001");

  return (
    <div className="ikkuna" data-valiin-ikkuna="">
      <div className="ikkuna__sisalto">
        <h2>{t("sel.valiin_otsikko")} — {linja.tunnus}</h2>

        {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

        <p className="dim">
          {valiinAlku}<strong>{edellinen.hitsinumero}</strong>{valiinLoppu}
        </p>

        <div className="lomake lomake--tiivis">
          <label>{t("sel.hitsinumero")}
            {/* Ehdotus on muokattavissa: kenttä voi tietää oikean numeron
                paremmin kuin laskenta (esim. isometrissä on jo merkintä). */}
            <input value={numero ?? ""} disabled={numero === null || kesken}
                   onChange={(e) => setNumero(e.target.value)} />
          </label>
          <label style={{ flex: "1 1 280px" }}>{tila === "suunniteltu" ? t("sel.miksi_tarvitaan") : t("hl.punakyna.miksi")}
            <input value={peruste} onChange={(e) => setPeruste(e.target.value)}
                   placeholder={t("sel.valiin_placeholder")} />
          </label>
          <fieldset className="lomake__valinta" data-valiin-tila={tila}>
            <legend>{t("nippu.tila")}</legend>
            <label><input type="radio" name="valiin-tila" value="hitsattu" checked={tila === "hitsattu"}
                          onChange={() => setTila("hitsattu")} /> {t("sel.hitsattu_nyt")}</label>
            <label><input type="radio" name="valiin-tila" value="suunniteltu" checked={tila === "suunniteltu"}
                          onChange={() => setTila("suunniteltu")} /> {t("sel.suunniteltu_myohemmin")}</label>
          </fieldset>
          <button className="nappi nappi--paa" data-valiin-lisaa=""
                  disabled={kesken || !numero || peruste.trim().length < 3}
                  onClick={() => void lisaa()}>{tila === "suunniteltu" ? t("sel.lisaa_suunniteltuna", { nro: numero ?? "" }) : t("sel.lisaa_nro", { nro: numero ?? "" })}</button>
          <button className="nappi nappi--kevyt" onClick={sulje}>{t("hl.peruuta")}</button>
        </div>

        <p className="ikkuna__vihje">
          {t("sel.valiin_vihje")}{tila === "suunniteltu" && <> {t("sel.valiin_vihje_suunniteltu")}</>}
        </p>
      </div>
    </div>
  );

  async function lisaa() {
    if (!numero) return;
    // 194 (päätös 62): numero on emon alinumero — etuliite on emon numero. Muu numero on eri asia kuin lisähitsi.
    const kanta = edellinen.hitsinumero.trim();
    if (!new RegExp("^" + kanta.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "[.,][0-9]+$").test(numero.trim())) {
      setVirhe(t("sel.alinumero_virhe", { kanta, nro: numero.trim() })); return;
    }
    setKesken(true); setVirhe(null);
    try {
      await api.kirjaaHitsi(tila === "suunniteltu"
        // 66: suunniteltu lisähitsi — ei päivää, ei lajia (119: laji on kirjaushetken valinta), ei osia.
        ? { linja_id: linja.id,
            ...(edellinen.piirustuslehti ? { piirustuslehti: edellinen.piirustuslehti } : {}),
            hitsinumero: numero.trim(), punakyna_peruste: peruste.trim(), suunniteltu: true, osat: [] }
        : { linja_id: linja.id,
            ...(edellinen.piirustuslehti ? { piirustuslehti: edellinen.piirustuslehti } : {}),
            hitsinumero: numero.trim(),
            // Laji peritään edellisestä: väliin lisätty hitsi on samaa työtä.
            // Kenttä voi korjata sen jälkikäteen hitsin muokkauksesta.
            ...(edellinen.hitsityyppi ? { hitsityyppi: edellinen.hitsityyppi } : {}),
            punakyna_peruste: peruste.trim(),
            // 4.10.2026: reitti vaatii päivän (400 «Päivä: pakollinen tieto») — lomake ei lähettänyt sitä, joten «+ hitsi»
            // ei ole koskaan tallentanut. Kentällä lisätty hitsi on tehty sinä päivänä kun se kirjataan.
            paiva: new Date().toISOString().slice(0, 10),
            osat: [] });
      await valmis();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("sel.lisays_virhe"));
    } finally { setKesken(false); }
  }
}

/* ── «Ei toteudu» (152/155, käyttöliittymä 21.9.2026) ────────
   Punakynän toinen puoli: suunniteltu sauma jää tekemättä.

   ⛔ Hitsi EI poistu. Rivi säilyy lokissa päätöksen tallenteena ja
   putoaa kattavuuden nimittäjästä — poistettu rivi ei kertoisi, että
   jotain päätettiin jättää tekemättä, vaan ettei sitä koskaan ollut.

   ⛔ Vain suunnitellulle. Nappi näkyy vain silloin, mutta portti on
   palvelimella (409 `hitsattu_ei_punakynaa`): napin puuttuminen ei ole
   rajaus, koska reitin voi kutsua ilman nappia.

   Työ ei pysähdy hyväksyntään: työnjohdon päätös pätee heti, ja
   asetetun hyväksyjän kuittaus on ehto luovutusaineistolle. */
function EiToteuduIkkuna({ hitsi, sulje, valmis }: {
  hitsi: Hitsi; sulje: () => void; valmis: () => Promise<void>;
}) {
  const [peruste, setPeruste] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);
  const [kesken, setKesken] = useState(false);

  return (
    <div className="ikkuna">
      <div className="ikkuna__sisalto">
        <h2>{t("sel.hitsi_ei_toteudu", { nro: hitsi.hitsinumero })}</h2>

        {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

        <div className="lomake lomake--tiivis">
          <label style={{ flex: "1 1 320px" }}>{t("sel.miksi_ei_tehda")}
            <input value={peruste} onChange={(e) => setPeruste(e.target.value)}
                   placeholder={t("sel.ei_toteudu_placeholder")} />
          </label>
          <button className="nappi nappi--paa"
                  disabled={kesken || peruste.trim().length < 3}
                  onClick={() => void merkitse()} data-ei-toteudu-esita="">{t("sel.merkitse_odottaa")}</button>
          <button className="nappi nappi--kevyt" onClick={sulje}>{t("hl.peruuta")}</button>
        </div>

        <p className="ikkuna__vihje">
          {t("sel.ei_toteudu_vihje")}
        </p>
      </div>
    </div>
  );

  async function merkitse() {
    setKesken(true); setVirhe(null);
    try {
      // 194 (päätös 62): rivin toiminto tekee ESITYKSEN («odottaa vahvistusta»); vahvistus on punakynän.
      await api.esitaEiToteudu(hitsi.id, peruste.trim());
      await valmis();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("sel.merkinta_virhe"));
    } finally { setKesken(false); }
  }
}

/* 218 (Toni 10.10.2026 «Tee kaikki 4»): poisto on esitys + vahvistus. Ilman avointa esitystä ikkuna kysyy perusteen ja
   tekee esityksen; avoimella esityksellä se näyttää esittäjän ja perusteen, ja työnjohtaja tai pääkäyttäjä vahvistaa tai
   hylkää (hylkäys vaatii perusteen). Vahvistus poistaa hitsin samassa transaktiossa; esitys jää tallenteeksi. */
function PoistoIkkuna({ hitsi, saaRatkaista, sulje, valmis }: {
  hitsi: Hitsi; saaRatkaista: boolean; sulje: () => void; valmis: () => Promise<void>;
}) {
  const v = hitsi.vahvistettava?.laji === "poisto" ? hitsi.vahvistettava : null;
  const [peruste, setPeruste] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);
  const [kesken, setKesken] = useState(false);

  async function tee(f: () => Promise<unknown>) {
    setKesken(true); setVirhe(null);
    try { await f(); await valmis(); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("sel.poisto_virhe")); }
    finally { setKesken(false); }
  }

  return (
    <div className="ikkuna" data-poisto-ikkuna={v ? "ratkaisu" : "esitys"}>
      <div className="ikkuna__sisalto">
        <h2>{v ? t("sel.ratkaise_poisto", { nro: hitsi.hitsinumero }) : t("sel.pyyda_poistoa_otsikko", { nro: hitsi.hitsinumero })}</h2>
        {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
        {v && (
          <p data-poisto-esitys="">
            <span className="dim">{v.kirjaaja ?? "?"} · {naytaPvm(v.hetki)}</span><br />{v.peruste}
          </p>
        )}
        {(!v || saaRatkaista) && (
          <div className="lomake lomake--tiivis">
            <label style={{ flex: "1 1 320px" }}>{v ? t("sel.poisto_hylkays_peruste") : t("sel.miksi_poistetaan")}
              <input value={peruste} onChange={(e) => setPeruste(e.target.value)} data-poisto-peruste="" />
            </label>
            {!v && (
              <button className="nappi nappi--paa" disabled={kesken || peruste.trim().length < 3} data-poisto-esita=""
                      onClick={() => void tee(() => api.esitaPoisto(hitsi.id, peruste.trim()))}>{t("sel.laheta_poistoesitys")}</button>
            )}
            {v && v.esitys_id != null && (
              <>
                <button className="nappi nappi--paa" disabled={kesken} data-poisto-vahvista=""
                        onClick={() => void tee(() => api.vahvistaPoisto(v.esitys_id!))}>{t("sel.vahvista_poisto")}</button>
                <button className="nappi nappi--kevyt" disabled={kesken || peruste.trim().length < 3} data-poisto-hylkaa=""
                        onClick={() => void tee(() => api.hylkaaPoisto(v.esitys_id!, peruste.trim()))}>{t("sel.hylkaa_poisto")}</button>
              </>
            )}
            <button className="nappi nappi--kevyt" onClick={sulje}>{t("hl.peruuta")}</button>
          </div>
        )}
        <p className="ikkuna__vihje">{v ? t("sel.poisto_ratkaisu_vihje") : t("sel.poisto_esitys_vihje")}</p>
      </div>
    </div>
  );
}

/* 7.10.2026 (Norjan erä): linjaan kirjatut puutteet linjan näkymässä — kuvaus, tila, vaikutus, kuvat. Kirjaus tapahtuu Puutteet-
   näkymässä (linja valinnaisena), ja kuva lisätään puutteen riviltä.
   Toni 17.10.2026 klo 07.40 kohta 3: suljettu (ratkaistu tai hylätty, esim. 248:n passivoimat) EI ole avointen joukossa eikä
   otsikon luvussa. Suljetut ovat omassa avattavassa osassaan, ja tila ja vaikutus näytetään käännettyinä, ei raakoina sanoina. */
function LinjanPuutteet({ linjaId, saaKorvata }: { linjaId: number; saaKorvata: boolean }) {
  const [rivit, setRivit] = useState<Puute[] | null>(null);
  const lataa = useCallback(() => { api.puutteet({ linja_id: linjaId, tila: "kaikki" }).then(setRivit).catch(() => setRivit(null)); }, [linjaId]);
  useEffect(lataa, [lataa]);
  if (!rivit) return null;
  const avoimet = rivit.filter((p) => p.tila === "avoin" || p.tila === "kaynnissa");
  const suljetut = rivit.filter((p) => p.tila !== "avoin" && p.tila !== "kaynnissa");
  // T655 (Toni 25.10.2026): korvattava puute (isometri puuttuu) kuitataan korvaavalla aineistolla tältä riviltä; korvattu näyttää lajin.
  const rivi = (p: Puute) => (
    <li key={p.id} data-linjan-puute={p.id} data-puute-tila={p.tila}>{puuteKuvausTeksti(p.kuvaus)}{" "}
      <span className="dim">· {nayttonimi("puute_tila", p.tila)} · {nayttonimi("puute_vaikutus", p.vaikutus)}{p.liitteita ? ` · ${t("sel.kuvia_n", { n: p.liitteita })}` : ""}</span>
      <KorvausMerkinta puute={p} />
      {saaKorvata && <>{" "}<KorvaavaKuittaus puute={p} muuttui={lataa} /></>}</li>
  );
  return (
    <section style={{ marginTop: 12 }} data-linjan-puutteet={avoimet.length} data-linjan-puutteet-suljetut={suljetut.length}>
      <h3 style={{ margin: "0 0 4px" }}>{t("sel.linjan_puutteet", { n: avoimet.length })}</h3>
      {avoimet.length === 0 ? <p className="dim" style={{ margin: 0 }}>{t("sel.ei_puutteita")}</p> : (
        <ul style={{ margin: 0, paddingLeft: 18 }}>{avoimet.map(rivi)}</ul>
      )}
      {suljetut.length > 0 && (
        <details style={{ marginTop: 4 }} data-linjan-puutteet-suljetut-lohko="">
          <summary className="dim">{t("sel.linjan_puutteet_suljetut", { n: suljetut.length })}</summary>
          <ul style={{ margin: 0, paddingLeft: 18 }}>{suljetut.map(rivi)}</ul>
        </details>
      )}
    </section>
  );
}
